import { db } from './db.js';
import { catalogStore } from './catalog-store.js';
import { fetchXtreamLists, restorePartial, isFresh, PARTIAL_RETRY_MS } from './catalog-fetch.js';
import { net } from './net.js';
import { settings, storage } from './storage.js';
import { epg } from './epg.js';
import { parseM3UAsync } from './m3u-async.js';
import { m3uTextFrom } from './tuner.js';
import {
    XtreamClient, normalizeLive, normalizeVod, normalizeSeries, normalizeCategory,
    normalizeVodInfo, normalizeSeriesInfo
} from './xtream.js';
import { Emitter, normalizeText, sha256, opaqueId, splitCast } from './utils.js';
import { variantKey, sortVariants } from './variants.js';
import { exceedsAge } from './age.js';
import { recommend, becauseRows, warmFeatures, similarity, genreTokens } from './recommend.js';
import { matchTrending, titleKey } from './trending.js';
import { subscriptionOf } from './subscription.js';
import { providers } from './providers.js';
import { mergeCatalogs, compositeId } from './merge.js';
import { blocksPlayback } from './subscription.js';
import { makeClient } from './clients.js';
import { confidentMatch } from './files.js';
import { presetOf } from './presets.js';
import { providerGroup, providerIcon } from './provider-groups.js';
import { withoutBlocked } from './content-block.js';
import { countryOf, countCountries, tidyEpisodes } from './country.js';

/* Tipos de proveedor que cuentan como «mis bibliotecas». */
const LIBRARY_TYPES = ['jellyfin', 'emby', 'plex', 'files', 'smb', 'local', 'dropbox', 'onedrive'];

const CATALOG_TTL = 6 * 3600 * 1000;

/** Los catálogos guardados antes de `logoArt` (listas M3U, que no traen datos de cuenta) lo reciben al leerlos. */
function tagLogoArt(data) {
    if (data && data.account === null) {
        (data.vod || []).forEach((x) => { if (x.logoArt === undefined) x.logoArt = true; });
        (data.series || []).forEach((x) => { if (x.logoArt === undefined) x.logoArt = true; });
    }
    return data;
}
/**
 * Título del catálogo → { key, year } para cruzarlo con créditos de TMDB: sin prefijo de país («ES| »), etiquetas entre
 * corchetes/paréntesis, calidad ni año final, y con el texto normalizado (sin acentos ni signos).
 */
export function matchKey(name) {
    let n = String(name || '');
    let year = '';
    const y = /[(\[-]\s*((?:19|20)\d{2})\s*[)\]]?\s*$/.exec(n) || /\(((?:19|20)\d{2})\)/.exec(n);
    if (y) year = y[1];
    n = n.replace(/^[A-Za-z]{2,3}\s*[|:]\s*/, '').replace(/\s*[\[(][^\])]*[\])]/g, ' ').replace(/\s*-\s*(?:19|20)\d{2}\s*$/, '')
        .replace(/\b(4k|uhd|hdr|1080p|720p|fhd|hd|dual|latino|castellano|subtitulado)\b/ig, ' ');
    const key = normalizeText(n).replace(/[^a-z0-9 ]/g, '').replace(/\s+/g, ' ').trim();
    const out = { key, year };
    // «Título 2004» sin paréntesis: puede ser el año o parte del título («Blade Runner 2049»), así que se ofrecen las dos lecturas.
    const bare = /^(.*\D) ((?:19|20)\d{2})$/.exec(key);
    if (bare && Number(bare[2]) <= 2035) { out.alt = bare[1].trim(); out.altYear = bare[2]; }
    return out;
}

const DETAILS_TTL = 24 * 3600 * 1000;
const MAX_RECENT = 20;
const TRENDING_TTL = 6 * 3600 * 1000;
const TOP_TTL = 30 * 60 * 1000; // el ranking del servidor se vuelve a pedir como mucho cada 30 min

/* Detección de categorías de contenido adulto por nombre. Cubre los términos y abreviaturas
   habituales en paneles IPTV en varios idiomas. Deliberadamente NO incluye palabras ambiguas
   (p. ej. "hot", "gay", "sexy" sueltas) para no marcar por error canales LGBT+, de música,
   deportivos, etc. */
const ADULT_WORD_RE = /\b(adult(?:s|os?|es)?|xxx|porn[oe]?|er[oó]tic[oa]?s?|hentai|playboy|brazzers|penthouse|fetish|milf)\b/i;
// "+18"/"18+": \b no sirve pegado a un símbolo (no hay frontera entre un espacio y "+"),
// así que delimitamos a mano por espacio/puntuación o inicio/fin de cadena.
const ADULT_SYMBOL_RE = /(?:^|[\s|:_/-])(?:\+18|18\+)(?:[\s|:_/-]|$)/i;
const ADULT_EMOJI_RE = /\u{1F51E}/u; // 🔞 (no apto para menores de 18)

export function isAdultCategoryName(name) {
    if (!name) return false;
    return ADULT_WORD_RE.test(name) || ADULT_SYMBOL_RE.test(name) || ADULT_EMOJI_RE.test(name);
}

// Resultado «todavía nada» de tasteRows: siempre el mismo objeto.
const NO_TASTE = Object.freeze({ recommended: Object.freeze([]), because: Object.freeze([]) });

/** Cede el hilo a la pantalla (que se pinte y responda) antes de seguir con un trabajo largo. */
function yieldToUi() {
    if (typeof scheduler !== 'undefined' && scheduler && typeof scheduler.yield === 'function') return scheduler.yield();
    return new Promise((resolve) => setTimeout(resolve, 0));
}

/** Recorre una lista cediendo la pantalla cada pocos milisegundos (para trabajos largos sobre catálogos de 100 000 títulos). */
async function sliceEach(list, fn, budgetMs = 8) {
    const clock = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());
    let t = clock();
    for (let i = 0; i < list.length; i++) {
        fn(list[i]);
        if ((i & 63) === 63 && clock() - t > budgetMs) { await yieldToUi(); t = clock(); }
    }
}

/* Versión de los datos precalculados de cada elemento (_s, region): sube si cambia normalizeText() o countryOf(). */
const PREP_VERSION = 1;

/** Calcula (o reaprovecha) el texto de búsqueda y el país de un elemento; `catName` es el nombre de su categoría. */
function prepareItem(item, catName) {
    let changed = false;
    if (item._pv !== PREP_VERSION || item._pn !== item.name || item._s === undefined) { item._s = normalizeText(item.name); changed = true; }
    if (item._pv !== PREP_VERSION || item._pn !== item.name || item._pc !== catName || item.region === undefined) { item.region = countryOf(item.name, catName); changed = true; }
    item._pv = PREP_VERSION;
    item._pn = item.name;
    item._pc = catName;
    return changed;
}

const prefixOf = (kind) => kind + ':';

export class Catalog extends Emitter {
    constructor() {
        super();
        this.profile = null;
        this.client = null;
        this.viewer = null;
        this.sync = null;
        this._pushTimers = {};
        // Simulación de caducidad ('expired' | 'expiring'), solo para probar: dura hasta cerrar la app.
        this.simulate = '';
        /** () => proveedores que se suman al catálogo además del principal. Lo pone main.js. */
        this.extraProfiles = null;
        this.reset();
    }

    reset() {
        this.live = [];
        this.vod = [];
        this.series = [];
        this.categories = { live: [], vod: [], series: [] };
        this._index = { live: new Map(), vod: new Map(), series: new Map() };
        this._catIndex = { live: new Map(), vod: new Map(), series: new Map() };
        this.loaded = false;
        this.loading = false;
        this.error = null;
        this.updatedAt = 0;
        this.account = null;
        this.favorites = new Set();
        this.hidden = new Set();
        this.progress = {};
        this.recent = [];
        this.parentalUnlocked = false;
        this._details = new Map();
        this._recommended = null;
        this._trendingMatch = null;
        this._topMatch = null;
        // Proveedores añadidos: id → { profile, client, data, error, loading }.
        this._primary = null;
        this.sources = new Map();
        this._reportAt = new Map(); // último aviso de reproducción por título (para no avisar a cada segundo)
        this.mergeStats = {};
    }

    get id() {
        return this.profile ? this.profile.id : null;
    }

    /** Clave de los datos de uso: proveedor + espectador (Mi lista, progreso, recientes). */
    get userKey() {
        return (this.id || '') + ':' + (this.viewer ? this.viewer.id : 'local');
    }

    get isKids() {
        return !!(this.viewer && this.viewer.kids);
    }

    /** Control parental efectivo: siempre en perfiles infantiles; si no, según ajustes y desbloqueo. */
    get lockActive() {
        if (this.isKids) return true;
        const parental = settings.get('parental');
        return !!(parental && parental.enabled && !this.parentalUnlocked);
    }

    /** Cambia de espectador manteniendo el catálogo cargado; recarga solo sus datos de uso. */
    async setViewer(viewer) {
        this.viewer = viewer || null;
        this.parentalUnlocked = false;
        // Lo guardado en este aparato basta para abrir el Inicio: lo del servidor (otros aparatos) se mezcla al llegar y avisa a las pantallas.
        if (this.profile) await this._restoreUserData({ waitPull: false });
        this.emit('progress');
        this.emit('change');
    }

    get isXtream() {
        return !!(this.profile && this.profile.type === 'xtream');
    }

    /** El proveedor en uso tiene cuenta a la que preguntar (todo menos las listas M3U). */
    get hasAccount() {
        return !!this.client;
    }

    /* ---------------- Carga ---------------- */

    async use(profile, { force = false } = {}) {
        if (!profile) {
            this.profile = null;
            this.client = null;
            this.reset();
            epg.reset();
            this.emit('change');
            return;
        }
        const switching = !this.profile || this.profile.id !== profile.id;
        this.profile = profile;
        this.client = makeClient(profile);
        if (switching) {
            this.reset();
            epg.reset();
            await this._restoreUserData();
        }
        await this.load({ force });
    }

    async _restoreUserData({ waitPull = true } = {}) {
        const key = this.userKey;
        let [favs, hidden, progress, recent] = await Promise.all([
            db.get('favorites', key + ':favorites'),
            db.get('favorites', key + ':hidden'),
            db.get('progress', key),
            db.get('meta', key + ':recent')
        ]);
        if (favs === undefined && progress === undefined) {
            // Datos de versiones anteriores (sin espectadores): los hereda el primer espectador que
            // se active y se borran, para que los espectadores creados después empiecen vacíos.
            const pid = this.id;
            [favs, hidden, progress, recent] = await Promise.all([
                db.get('favorites', pid + ':favorites'),
                db.get('favorites', pid + ':hidden'),
                db.get('progress', pid),
                db.get('meta', pid + ':recent')
            ]);
            if (favs !== undefined || progress !== undefined || recent !== undefined) {
                await Promise.all([
                    db.put('favorites', key + ':favorites', favs || []),
                    db.put('favorites', key + ':hidden', hidden || []),
                    db.put('progress', key, progress || {}),
                    db.put('meta', key + ':recent', recent || []),
                    db.delete('favorites', pid + ':favorites'),
                    db.delete('favorites', pid + ':hidden'),
                    db.delete('progress', pid),
                    db.delete('meta', pid + ':recent')
                ]);
            }
        }
        this.favorites = new Set(favs || []);
        this.hidden = new Set(hidden || []);
        this.progress = progress || {};
        this.recent = recent || [];
        if (waitPull) await this._pullUserData();
        else this._pullUserData().catch(() => {});
    }

    /* ---------------- Sincronización de datos de uso con la cuenta ---------------- */

    async _pullUserData() {
        if (!this.sync || !this.viewer || !this.id) return;
        const viewerId = this.viewer.id;
        const pid = this.id;
        let remote;
        try {
            remote = await this.sync.pullUserData(viewerId, pid);
        } catch (e) {
            return;
        }
        if (!remote || this.viewer.id !== viewerId || this.id !== pid) return;
        const empty = !remote.favorites && !remote.hidden && !remote.progress && !remote.recent;
        if (empty) {
            // Primera vez en el servidor: subir lo que hay en el dispositivo.
            if (this.favorites.size) this._push('favorites');
            if (this.hidden.size) this._push('hidden');
            if (Object.keys(this.progress).length) this._push('progress');
            if (this.recent.length) this._push('recent');
            return;
        }
        if (remote.favorites) this.favorites = new Set(remote.favorites);
        if (remote.hidden) this.hidden = new Set(remote.hidden);
        if (remote.recent) this.recent = remote.recent;
        if (remote.progress) {
            const merged = Object.assign({}, this.progress);
            Object.keys(remote.progress).forEach((k) => {
                const r = remote.progress[k];
                if (!merged[k] || (r.updated || 0) >= (merged[k].updated || 0)) merged[k] = r;
            });
            this.progress = merged;
        }
        const key = this.userKey;
        await Promise.all([
            db.put('favorites', key + ':favorites', Array.from(this.favorites)),
            db.put('favorites', key + ':hidden', Array.from(this.hidden)),
            db.put('progress', key, this.progress),
            db.put('meta', key + ':recent', this.recent)
        ]);
        this.emit('progress');
        this.emit('change');
    }

    /** Trae del servidor el progreso guardado desde otros dispositivos (como mucho cada 15 s). */
    async pullProgress({ force = false } = {}) {
        if (!this.sync || !this.viewer || !this.id) return false;
        if (!force && Date.now() - (this._lastProgressPull || 0) < 15000) return false;
        this._lastProgressPull = Date.now();
        const viewerId = this.viewer.id;
        const pid = this.id;
        let remote;
        try {
            remote = await this.sync.pullUserData(viewerId, pid);
        } catch (e) {
            return false;
        }
        if (!remote || !remote.progress || this.viewer.id !== viewerId || this.id !== pid) return false;
        let changed = false;
        Object.keys(remote.progress).forEach((k) => {
            const r = remote.progress[k];
            const l = this.progress[k];
            if (!l || (r.updated || 0) > (l.updated || 0)) {
                this.progress[k] = r;
                changed = true;
            }
        });
        if (changed) {
            await db.put('progress', this.userKey, this.progress);
            this.emit('progress');
            this.emit('change');
        }
        return changed;
    }

    _push(key) {
        if (!this.sync || !this.viewer || !this.id) return;
        clearTimeout(this._pushTimers[key]);
        const viewerId = this.viewer.id;
        const pid = this.id;
        this._pushTimers[key] = setTimeout(() => {
            delete this._pushTimers[key];
            this.sync.pushUserData(viewerId, pid, key, this._userData(key)).catch((e) => console.warn('[sync]', key, e.message));
        }, key === 'progress' ? 3000 : 400);
    }

    _userData(key) {
        if (key === 'favorites') return Array.from(this.favorites);
        if (key === 'hidden') return Array.from(this.hidden);
        if (key === 'progress') return this.progress;
        return this.recent;
    }

    /** Envía sin esperar lo pendiente (al ocultar la pestaña o cerrar el reproductor). */
    flushSync() {
        Object.keys(this._pushTimers).forEach((key) => {
            clearTimeout(this._pushTimers[key]);
            delete this._pushTimers[key];
            if (this.sync && this.viewer && this.id) this.sync.pushUserData(this.viewer.id, this.id, key, this._userData(key)).catch(() => {});
        });
    }

    async load({ force = false } = {}) {
        if (this.loading) return;
        this.loading = true;
        this.error = null;
        this.emit('loading', true);
        try {
            const pid = this.id;
            let data = force ? null : tagLogoArt(await catalogStore.load(pid));
            if (data && data.live && (isFresh(data, CATALOG_TTL) || !navigator.onLine)) {
                const computed = await this._prepareItems(data);
                this._apply(data);
                // Caché anterior sin los datos precalculados: se guarda una vez con ellos para que las próximas aperturas sean rápidas.
                // Se guarda bastante después: a los 3 s el usuario suele estar ya navegando y la escritura (60 000 títulos) le robaba el hilo.
                if (computed || data.__legacy) this.whenCalm(() => catalogStore.save(pid, data).catch(() => {}), { minDelay: 12000, timeout: 10000 });
            } else {
                if (data && data.live) {
                    // Mostrar caché al instante y refrescar en segundo plano.
                    await this._prepareItems(data);
                    this._apply(data);
                    this.loading = false;
                    this.emit('loading', false);
                    this.whenCalm(() => this._refreshInBackground());
                    this.whenCalm(() => this.loadExtras().catch(() => {}));
                    return;
                }
                const fresh = await this._keepWhatWeHad(pid, await this._fetchAll());
                await this._prepareItems(fresh);
                this._apply(fresh);
                providers.record(pid, fresh.account);
                await catalogStore.save(pid, fresh);
            }
            // La guía (EPG) y los proveedores añadidos no hacen falta para la primera pantalla: esperan a que la interfaz esté tranquila.
            this.whenCalm(() => epg.restore(pid).then(() => {
                this.emit('change');
                if (epg.isStale() && navigator.onLine) this.loadEpg().catch(() => {});
            }));
            // Los proveedores añadidos se cargan después: el principal se ve sin esperarlos.
            this.whenCalm(() => this.loadExtras({ force }).catch(() => {}));
        } catch (err) {
            console.error('[catalog] load error', err);
            this.error = err;
        } finally {
            this.loading = false;
            this.emit('loading', false);
            this.emit('change');
        }
    }

    /** Si alguna lista no se pudo bajar, se conserva la que ya había guardada de ese tipo (mejor catálogo viejo que sin películas). */
    async _keepWhatWeHad(pid, fresh) {
        if (fresh && fresh.partial && fresh.partial.length) restorePartial(fresh, await catalogStore.load(pid).catch(() => null));
        return fresh;
    }

    async _refreshInBackground() {
        try {
            const fresh = await this._keepWhatWeHad(this.id, await this._fetchAll());
            await this._prepareItems(fresh);
            this._apply(fresh);
            providers.record(this.id, fresh.account);
            await catalogStore.save(this.id, fresh);
            this.emit('change');
        } catch (err) {
            console.warn('[catalog] background refresh failed', err);
        }
    }

    async refresh() {
        return this.load({ force: true });
    }

    /**
     * Actualización desatendida: vuelve a pedir el catálogo del principal y de los añadidos que llevan más de `maxAgeMs` sin
     * actualizarse, sin pantallas de carga (se ve el catálogo de siempre hasta que llega el nuevo). Devuelve true si pidió algo.
     */
    async refreshStale({ maxAgeMs = 24 * 3600 * 1000 } = {}) {
        if (!this.profile || this.loading) return false;
        const now = Date.now();
        let did = false;
        const mine = (this._primary && this._primary.updatedAt) || catalogStore.updatedAt(this.id);
        const mineAge = this._primary && this._primary.partial && this._primary.partial.length ? PARTIAL_RETRY_MS : maxAgeMs;
        if (now - mine >= mineAge) { await this._refreshInBackground(); did = true; }
        const stale = Array.from(this.sources.values()).some((src) => !src.loading && src.data && now - (src.data.updatedAt || 0) >= (src.data.partial && src.data.partial.length ? PARTIAL_RETRY_MS : maxAgeMs));
        if (stale) { await this.loadExtras({ force: true }).catch(() => {}); did = true; }
        return did;
    }

    async _fetchAll() {
        return this._fetchFor(this.profile, this.client);
    }

    _fetchFor(profile, client) {
        // Las bibliotecas propias (Jellyfin, Emby, Plex) traen el catálogo ya normalizado.
        if (client && client.fetchCatalog) return client.fetchCatalog();
        return profile.type === 'xtream' ? this._fetchXtream(client) : this._fetchM3u(profile);
    }

    async _fetchXtream(c) {
        const auth = await c.auth();
        const got = await fetchXtreamLists(c);
        return {
            updatedAt: Date.now(),
            account: { user: auth.user_info || {}, server: auth.server_info || {} },
            live: got.live.map(normalizeLive),
            vod: got.vod.map(normalizeVod),
            series: got.series.map(normalizeSeries),
            categories: {
                live: got.categories.live.map(normalizeCategory),
                vod: got.categories.vod.map(normalizeCategory),
                series: got.categories.series.map(normalizeCategory)
            },
            partial: got.partial // tipos que no se pudieron bajar (se reintenta pronto)
        };
    }

    async _fetchM3u(profile) {
        const text = m3uTextFrom(await net.fetchText(profile.m3uUrl, { timeout: 120000 }), profile.m3uUrl);
        // Las listas públicas cambian a menudo: sus canales se identifican por nombre, no por posición.
        const parsed = await parseM3UAsync(text, { stableIds: !!presetOf(profile) });
        if (!parsed.live.length && !parsed.vod.length && !parsed.series.length) {
            throw new Error('empty-m3u');
        }
        return {
            updatedAt: Date.now(),
            account: null,
            live: parsed.live,
            vod: parsed.vod,
            series: parsed.series,
            categories: parsed.categories,
            epgUrl: parsed.epgUrl || ''
        };
    }

    /** Dirección de la guía de un proveedor: la de su panel, la indicada a mano o la de su lista. */
    _epgUrlFor(profile, client, data) {
        if (client) return client.xmltvUrl();
        return profile.epgUrl || (data && data.epgUrl) || '';
    }

    /* ---------------- Varios proveedores ---------------- */

    /**
     * Carga los proveedores añadidos y los une al catálogo del principal (core/merge.js). Usa la
     * misma caché que cuando ese proveedor es el principal. Si uno falla, los demás siguen.
     */
    async loadExtras({ force = false } = {}) {
        if (!this.profile) return;
        const pid = this.id;
        const list = (this.extraProfiles ? this.extraProfiles() : []).filter((p) => p && p.id !== pid);
        const wanted = new Set(list.map((p) => p.id));
        let dropped = false;
        Array.from(this.sources.keys()).forEach((id) => {
            if (!wanted.has(id)) {
                this.sources.delete(id);
                epg.dropExtra(id);
                dropped = true;
            }
        });
        if (dropped && this._primary) {
            this._merge();
            this.emit('change');
        }
        await Promise.all(list.map(async (profile) => {
            let src = this.sources.get(profile.id);
            if (!src) {
                src = { profile, client: null, data: null, error: null, loading: false };
                this.sources.set(profile.id, src);
            }
            src.profile = profile;
            src.client = makeClient(profile);
            src.loading = true;
            this.emit('sources');
            const current = () => this.id === pid && this.sources.get(profile.id) === src;
            const merge = () => {
                if (!current() || !this._primary) return;
                this._merge();
                this.emit('change');
            };
            try {
                let data = force ? null : tagLogoArt(await catalogStore.load(profile.id));
                const has = !!(data && data.live);
                const fresh = has && (isFresh(data, CATALOG_TTL) || navigator.onLine === false);
                if (has && src.data !== data) {
                    src.data = data;
                    merge();
                }
                if (!fresh) {
                    data = await this._keepWhatWeHad(profile.id, await this._fetchFor(profile, src.client));
                    providers.record(profile.id, data.account);
                    await catalogStore.save(profile.id, data);
                    src.data = data;
                    merge();
                }
                src.error = null;
                // Su guía se suma a la del principal (sin esperar a que termine).
                const guide = this._epgUrlFor(profile, src.client, src.data);
                if (guide && current()) epg.loadExtra(profile.id, guide, { force }).catch((e) => console.warn('[catalog] guía no disponible', profile.name, e && e.message));
            } catch (err) {
                console.warn('[catalog] proveedor añadido no disponible', profile.name, err);
                src.error = err;
            } finally {
                src.loading = false;
                this.emit('sources');
            }
        }));
    }

    /** Proveedor añadido del que sale un título; null si es del principal. */
    sourceOf(item) {
        return item && item.source ? this.sources.get(item.source) || null : null;
    }

    /** Perfil del proveedor al que pertenece un título. */
    profileOf(item) {
        const src = this.sourceOf(item);
        return src ? src.profile : this.profile;
    }

    _client(item) {
        if (item && item.source) {
            const src = this.sources.get(item.source);
            return src ? src.client : null;
        }
        return this.client;
    }

    /** Qué hacer con TMDB para un título. 'skip': vídeos personales (casilla del proveedor), nunca. 'strict': carpetas propias, solo si el título
        de TMDB se parece de verdad al del fichero (TMDB siempre devuelve «lo más parecido», aunque no tenga nada que ver). 'free': el resto. */
    /** ¿TMDB apagado para las fichas? Con «No usar TMDB», o con «Usar OMDb si está configurado» cuando hay OMDb (si no lo hay, TMDB sigue como siempre). */
    _tmdbOff() {
        const m = settings.get('tmdbMode');
        return m === 'off' || (m === 'omdb' && !!this.omdbSource);
    }

    _tmdbPolicy(item) {
        const p = item ? this.profileOf(item) : null;
        if (!p || ['files', 'smb', 'local', 'dropbox', 'onedrive'].indexOf(p.type) === -1) return 'free';
        if (item.edited && item.tmdb) return 'free'; // enlazado a mano con TMDB desde «Editar ficha»
        return p.personal && p.personal !== '0' ? 'skip' : 'strict';
    }

    /**
     * Valoraciones (IMDb, Rotten Tomatoes, Metacritic) y premios de OMDb, si el servidor tiene su clave. Lo que la ficha ya traiga
     * no se pisa: solo se rellena lo que falte (sinopsis, reparto, edad…). Una consulta por título (el servidor la guarda unos días).
     */
    async _enrichOmdb(kind, info, base = info) {
        if (!this.omdbSource || !info || info.omdbOk) return info;
        if (this._tmdbPolicy(base) === 'skip') return Object.assign({}, info, { omdbOk: 1 }); // vídeos personales: nada de servicios externos
        let x;
        try { x = await this.omdbSource(kind, info); } catch (e) { return info; }
        if (!x || !x.imdbId) return Object.assign({}, info, { omdbOk: 1 });
        if (this._tmdbPolicy(base) === 'strict' && !confidentMatch(info, { tmdbId: 1, title: x.title, originalTitle: x.title, year: x.year })) return Object.assign({}, info, { omdbOk: 1 });
        const out = Object.assign({}, info, {
            omdbOk: 1,
            srcOmdb: 1,
            imdb: info.imdb || x.imdbId,
            imdbRating: x.imdbRating || 0, imdbVotes: x.imdbVotes || '', rt: x.rt || 0, metascore: x.metascore || 0, awards: x.awards || '',
            plot: info.plot || x.plot,
            age: info.age || x.rated,
            genre: info.genre || x.genres,
            year: info.year || x.year,
            director: info.director || x.director,
            country: info.country || x.country,
            rating: info.rating || x.imdbRating || 0,
            durationSecs: info.durationSecs || (kind === 'vod' && x.runtime ? x.runtime * 60 : 0)
        });
        if (!(info.cast || (Array.isArray(info.castList) && info.castList.length)) && x.cast.length) {
            out.cast = x.cast.map((c) => c.name).join(', ');
            out.castList = x.cast;
        }
        return out;
    }

    /**
     * Completa lo que falte con el catálogo de Filoteca (Wikidata, CC0): reparto con personajes, dirección, géneros, países, duración,
     * año e identificadores. Solo por identificador exacto (IMDb/TMDB) y nunca pisa lo que la ficha ya trae. Solo en títulos «libres»
     * (no en vídeos personales ni en carpetas propias con coincidencia dudosa).
     */
    async _enrichCatalog(kind, info, base = info) {
        if (!this.catalogSource || !info || info.catalogOk) return info;
        if (this._tmdbPolicy(base) !== 'free') return Object.assign({}, info, { catalogOk: 1 });
        let x;
        try { x = await this.catalogSource(kind, info); } catch (e) { return info; } // sin red: se reintenta otro día
        if (!x) return Object.assign({}, info, { catalogOk: 1 });
        const has = (v) => Array.isArray(v) ? v.length > 0 : !!v;
        const out = Object.assign({}, info, {
            catalogOk: 1, srcCatalog: 1, catalogWork: x.workId,
            imdb: info.imdb || x.imdbId || info.imdb,
            tmdb: info.tmdb || (kind === 'series' ? x.tmdbTvId : x.tmdbMovieId) || info.tmdb,
            year: info.year || (x.year ? String(x.year) : info.year),
            director: info.director || x.directors.join(', '),
            genre: info.genre || x.genres.join(', '),
            country: info.country || x.countries.join(', '),
            durationSecs: info.durationSecs || (kind === 'vod' && x.minutes ? x.minutes * 60 : 0)
        });
        if (!has(info.plot) && x.synopsis && x.synopsis.text) { out.plot = x.synopsis.text; out.plotSource = { name: 'Wikipedia', license: x.synopsis.license || 'CC BY-SA 4.0', licenseUrl: x.synopsis.licenseUrl || '', url: x.synopsis.sourceUrl || '', attribution: x.synopsis.attribution || '' }; }
        if (!has(info.cast) && !has(info.castList) && x.cast.length) { out.cast = x.cast.map((c) => c.name).join(', '); out.castList = x.cast.map((c) => ({ name: c.name, character: c.character })); }
        return out;
    }

    /** ¿La ficha ya trae lo básico (sinopsis, imagen y reparto)? Entonces TMDB no hace falta. */
    _hasBasics(info) {
        return !!(info && info.plot && (info.backdrop || info.poster) && ((Array.isArray(info.castList) && info.castList.length) || info.cast));
    }

    /** Olvida la ficha completa de un título (memoria y guardada): se rehace al abrirla, p. ej. tras enlazarlo a mano con TMDB. */
    async dropDetails(kind, id) {
        const key = this.id + ':' + kind + ':' + id;
        this._details.delete(key);
        try { await db.delete('details', key); } catch (e) { /* sin base de datos */ }
    }

    _rawId(item) {
        return item.rawId != null ? item.rawId : item.id;
    }

    /** Estado de la suscripción de un proveedor cualquiera (el principal o uno añadido). */
    subscriptionOfSource(pid) {
        if (!pid || pid === this.id) return this.subscription();
        const src = this.sources.get(pid);
        return src ? providers.get(src.profile).sub : subscriptionOf(null);
    }

    /**
     * Dónde se puede reproducir un título: su proveedor y los que lo tienen como alternativa.
     * Primero los que tienen la suscripción en vigor. Cada ruta: { pid, name, blocked, target },
     * donde target es lo que hay que pasar a liveUrl/vodUrl/episodeUrl.
     */
    playRoutes(item) {
        if (!item) return [];
        const own = item.source || this.id;
        const routes = [{ pid: own, target: item }];
        (item.alt || []).forEach((a) => {
            if (!a.source) {
                // Otra emisión del mismo canal en la misma lista.
                if (a.url) routes.push({ pid: own, target: Object.assign({}, item, { url: a.url, alt: undefined }) });
                return;
            }
            if (!this.sources.has(a.source)) return;
            routes.push({ pid: a.source, target: { id: compositeId(a.source, a.id), rawId: a.id, source: a.source, name: a.name, ext: a.ext, url: a.url, part: a.part, archive: false } });
        });
        routes.forEach((r, i) => {
            r.key = r.pid + '|' + (r.target.url || r.target.id);
            const profile = r.pid === this.id ? this.profile : (this.sources.get(r.pid) || {}).profile;
            r.name = profile ? profile.name : '';
            r.blocked = blocksPlayback(this.subscriptionOfSource(r.pid));
            r.order = i;
        });
        // Con «Solo mis bibliotecas», un título que también está en una biblioteca se reproduce primero desde ella.
        const libs = this.onlyLibraries ? new Set(this.libraryProviderIds()) : null;
        const lib = (r) => (libs && libs.has(r.pid) ? 0 : 1);
        return routes.sort((a, b) => (a.blocked ? 1 : 0) - (b.blocked ? 1 : 0) || (libs ? lib(a) - lib(b) : 0) || a.order - b.order);
    }

    /** Ficha de una ruta de reproducción (para saber sus códecs); la del propio título si es la suya. */
    async routeInfo(kind, target) {
        if (this.get(kind, target.id)) return this._detailInfo(kind, target.id);
        const key = this.id + ':' + kind + ':' + target.id;
        if (this._details.has(key)) return this._details.get(key);
        const client = this._client(target);
        if (!client) return target;
        try {
            const data = this._tagEpisodes(await this._clientInfo(client, kind, target.rawId, target), target);
            this._details.set(key, data);
            return data;
        } catch (e) {
            return target;
        }
    }

    /** Ficha normalizada que da un cliente: la suya si la sabe dar, o la de Xtream. */
    async _clientInfo(client, kind, rawId, base) {
        if (client.info) return client.info(kind, rawId, base);
        const raw = kind === 'vod' ? await client.vodInfo(rawId) : await client.seriesInfo(rawId);
        return kind === 'vod' ? normalizeVodInfo(raw, base) : normalizeSeriesInfo(raw, base);
    }

    /** El mismo episodio en el proveedor alternativo de una serie, o null si allí no está. */
    async altEpisode(episode, target) {
        const info = await this.routeInfo('series', target);
        return this.findEpisode(info, episode.season, episode.num);
    }

    /** Lo visto de los episodios según el servidor propio (antes de ponerles el prefijo del proveedor). */
    _adoptEpisodes(info, base) {
        if (!this.syncServers || !info || !info.seasons) return;
        let changed = false;
        info.seasons.forEach((season) => season.episodes.forEach((ep) => {
            if (!ep.server) return;
            const view = Object.assign({}, ep, { id: base && base.source ? compositeId(base.source, ep.id) : ep.id });
            const extra = { seriesId: base ? base.id : ep.seriesId, season: ep.season, episode: ep.num, title: ep.title, poster: base ? base.poster : '' };
            if (this._adoptOne('episode', view, ep.server, extra)) changed = true;
        }));
        if (changed) this._saveProgress().catch(() => {});
    }

    /** Los episodios de una serie de un proveedor añadido llevan su mismo prefijo y origen. */
    _tagEpisodes(info, base) {
        this._adoptEpisodes(info, base);
        if (!base || !base.source || !info || !info.seasons) return info;
        info.seasons.forEach((season) => season.episodes.forEach((ep) => {
            if (ep.source) return;
            ep.rawId = ep.id;
            ep.id = compositeId(base.source, ep.id);
            ep.source = base.source;
        }));
        return info;
    }

    /* ---------------- Suscripción del proveedor en uso ---------------- */

    /** Estado de la suscripción con lo último que ha dicho el panel (o el simulado). */
    subscription() {
        return subscriptionOf(this.account, { simulate: this.simulate });
    }

    /**
     * Vuelve a preguntar al panel por el estado de la cuenta (una petición ligera, sin recargar el
     * catálogo). Si no responde, se queda con lo que ya se sabía.
     */
    async checkSubscription() {
        if (this.simulate || !this.profile || !this.hasAccount) return this.subscription();
        const pid = this.id;
        const before = this.subscription().state;
        const r = await providers.check(this.profile);
        if (pid !== this.id) return this.subscription();
        const known = providers.status[pid];
        if (!r.error && known && known.account) this.account = known.account;
        else if (r.error === 'auth') this.account = { user: Object.assign({}, this.account && this.account.user, { status: 'Disabled' }), server: (this.account && this.account.server) || {} };
        this._subChecked = Date.now();
        if (this.subscription().state !== before) this.emit('change');
        return this.subscription();
    }

    /** Como checkSubscription, pero como mucho una vez cada 10 minutos (al abrir Inicio). */
    checkSubscriptionSoon() {
        if (Date.now() - (this._subChecked || 0) < 600000) return Promise.resolve(this.subscription());
        this._subChecked = Date.now();
        return this.checkSubscription().catch(() => this.subscription());
    }

    /* ---------------- Identificadores públicos (URL) ---------------- */

    /** Identificador opaco de un elemento para las URL de la web (depende también del proveedor). */
    uidOf(kind, id) {
        return opaqueId((this.id || '') + '|' + kind + '|' + id);
    }

    /** Resuelve un identificador opaco a { kind, id }, o null. El índice se construye una vez por catálogo. */
    byUid(uid) {
        if (!/^[0-9a-f]{32}$/.test(String(uid || ''))) return null;
        const stamp = (this.id || '') + ':' + this.live.length + ':' + this.vod.length + ':' + this.series.length;
        if (!this._uidIndex || this._uidStamp !== stamp) {
            const map = new Map();
            ['live', 'vod', 'series'].forEach((kind) => {
                const items = this[kind];
                for (let i = 0; i < items.length; i++) map.set(this.uidOf(kind, items[i].id), { kind, id: items[i].id });
            });
            this._uidIndex = map;
            this._uidStamp = stamp;
        }
        return this._uidIndex.get(uid) || null;
    }

    /* ---------------- Clasificación por edades ---------------- */

    /** Edad máxima del espectador actual: la suya, o 12 en perfiles infantiles; null = sin límite. */
    get maxAge() {
        const v = this.viewer;
        if (!v) return null;
        if (v.maxAge != null && v.maxAge !== '') return Number(v.maxAge);
        return v.kids ? 12 : null;
    }

    /** true si la clasificación del título supera la edad máxima del espectador. */
    ageBlocked(info) {
        return exceedsAge(info && info.age, this.maxAge);
    }

    /* ---------------- Reparto ---------------- */

    /**
     * Índice de personas (actores y directores) de este proveedor, construido con las fichas ya
     * consultadas (caché de detalles). Se reconstruye cuando cambia esa caché.
     * Map: nombre normalizado → { name, entries: [{ kind, id, role }] }.
     */
    async _peopleIndex() {
        if (this._people && this._peopleFor === this.id) return this._people;
        const prefix = (this.id || '') + ':';
        const index = new Map();
        if (!this.id) return index;
        const rows = await db.getAll('details');
        const add = (name, kind, id, role, meta) => {
            const norm = normalizeText(name);
            if (!norm) return;
            let p = index.get(norm);
            if (!p) { p = { name, entries: [] }; index.set(norm, p); }
            if (!p.entries.some((e) => e.kind === kind && e.id === id && e.role === role)) p.entries.push({ kind, id, role, age: meta.age, year: meta.year });
        };
        for (const r of rows) {
            if (typeof r.key !== 'string' || r.key.indexOf(prefix) !== 0) continue;
            const rest = r.key.slice(prefix.length);
            const sep = rest.indexOf(':');
            if (sep === -1) continue;
            const kind = rest.slice(0, sep);
            const id = rest.slice(sep + 1);
            if (kind !== 'vod' && kind !== 'series') continue;
            const data = r.value && r.value.data;
            if (!data) continue;
            const meta = { age: data.age, year: data.year };
            splitCast(data.cast).forEach((n) => add(n, kind, id, 'actor', meta));
            splitCast(data.director).forEach((n) => add(n, kind, id, 'director', meta));
        }
        this._people = index;
        this._peopleFor = this.id;
        return index;
    }

    /**
     * Películas y series de este proveedor en las que `name` actúa o dirige, a partir de las fichas
     * ya consultadas. No hace peticiones: la página de la persona ofrece un rastreo del catálogo
     * completo que va rellenando esa caché. Cada resultado lleva `roles` (actor y/o director).
     */
    /** Índice título-limpio → elementos del catálogo (películas y series), para cruzar filmografías. Se rehace al cambiar el catálogo. */
    _titleIndex() {
        if (this._titleIdx) return this._titleIdx;
        const idx = { vod: new Map(), series: new Map() };
        ['vod', 'series'].forEach((kind) => {
            this[kind].forEach((item) => {
                const m = matchKey(item.name);
                if (!m.key) return;
                const add = (key, year) => {
                    const rec = { item, year: Number(item.year) || Number(m.year) || Number(year) || 0 };
                    const list = idx[kind].get(key);
                    if (list) list.push(rec); else idx[kind].set(key, [rec]);
                };
                add(m.key, 0);
                if (m.alt && !m.year && !Number(item.year)) add(m.alt, m.altYear);
            });
        });
        this._titleIdx = idx;
        return idx;
    }

    /**
     * Películas y series del catálogo en las que trabaja `name` (como actor o director), según TMDB: una sola consulta con toda
     * su filmografía que se cruza con el catálogo por título y año, sin abrir las fichas una a una.
     * → { person: { name, photo } | null, items: [{ kind, id, item, year, roles, character }] }; items vacío si no hay TMDB.
     */
    async personCredits(name) {
        if (!this.personSource || !name) return { person: null, items: [] };
        let info;
        try {
            info = await this.personSource(name);
        } catch (e) {
            return { person: null, items: [] };
        }
        if (!info || !Array.isArray(info.credits)) return { person: null, items: [] };
        const idx = this._titleIndex();
        const out = new Map();
        for (const c of info.credits) {
            const list = idx[c.kind];
            if (!list) continue;
            const seenKeys = new Set();
            for (const t of [c.title, c.originalTitle]) {
                const k = matchKey(t).key;
                if (!k || seenKeys.has(k)) continue;
                seenKeys.add(k);
                for (const rec of list.get(k) || []) {
                    const cy = Number(c.year) || 0;
                    // Con año en los dos lados debe coincidir (±1: estrenos a caballo de año); si falta alguno, vale el título.
                    if (cy && rec.year && Math.abs(cy - rec.year) > 1) continue;
                    const key = c.kind + ':' + rec.item.id;
                    let row = out.get(key);
                    if (!row) {
                        row = { kind: c.kind, id: rec.item.id, item: rec.item, year: cy || rec.year || 0, roles: [], character: '' };
                        out.set(key, row);
                    }
                    if (row.roles.indexOf(c.role) === -1) row.roles.push(c.role);
                    if (c.role === 'actor' && c.character && !row.character) row.character = c.character;
                }
            }
        }
        // Solo lo que el espectador puede ver (sin ocultos, categorías bloqueadas ni contenido adulto en perfiles infantiles).
        const visible = { vod: new Set(this.visible('vod').map((m) => m.id)), series: new Set(this.visible('series').map((m) => m.id)) };
        const items = Array.from(out.values()).filter((r) => visible[r.kind].has(r.item.id));
        items.sort((a, b) => b.year - a.year || String(a.item.name).localeCompare(String(b.item.name)));
        return { person: { name: info.name || name, photo: info.photo || '', department: info.department || '' }, items };
    }

    /**
     * Persona de TMDB que coincide con lo escrito en el buscador y tiene títulos en el catálogo, aunque todavía no se
     * hayan abierto fichas con su nombre. → { name, count, roles, photo } o null (sin TMDB, sin coincidencia o sin títulos).
     */
    async remotePerson(query) {
        const q = normalizeText(query);
        if (!this.personSource || q.length < 4) return null;
        const res = await this.personCredits(query);
        if (!res.person || !res.items.length) return null;
        // TMDB devuelve «lo más parecido»: solo vale si el nombre contiene todo lo escrito (si no, «matrix» daría una persona al azar).
        const nn = normalizeText(res.person.name);
        if (!q.split(' ').every((x) => nn.indexOf(x) !== -1)) return null;
        const roles = [];
        res.items.forEach((it) => it.roles.forEach((r) => { if (roles.indexOf(r) === -1) roles.push(r); }));
        return { name: res.person.name, count: res.items.length, roles, photo: res.person.photo || '' };
    }

    async actorFilmography(name) {
        const norm = normalizeText(name);
        if (!norm || !this.id) return [];
        const person = (await this._peopleIndex()).get(norm);
        if (!person) return [];
        const byKey = new Map();
        for (const e of person.entries) {
            const key = e.kind + ':' + e.id;
            let row = byKey.get(key);
            if (!row) {
                const item = this.get(e.kind, e.id);
                if (!item) continue; // ya no está en el catálogo (o está oculto/bloqueado)
                if (this.ageBlocked({ age: e.age })) continue;
                row = { kind: e.kind, id: e.id, item, year: Number(e.year || item.year) || 0, roles: [] };
                byKey.set(key, row);
            }
            if (row.roles.indexOf(e.role) === -1) row.roles.push(e.role);
        }
        const out = Array.from(byKey.values());
        out.sort((a, b) => b.year - a.year || String(a.item.name).localeCompare(String(b.item.name)));
        return out;
    }

    /** ¿Se ha completado alguna vez el rastreo de repartos de este proveedor? */
    castScanDone() {
        const m = storage.get('castScan', {}) || {};
        return !!(this.id && m[this.id]);
    }

    /**
     * Consulta la ficha de cada película y serie visible (varias a la vez) para conocer repartos y
     * directores. `onProgress(done, total)` se llama a menudo; `shouldStop()` permite detenerlo.
     * Devuelve true si llegó hasta el final (entonces se recuerda para no ofrecerlo de nuevo).
     */
    async scanDetails({ parallel = 3, onProgress, shouldStop } = {}) {
        const todo = this.visible('vod').map((m) => ['vod', m.id]).concat(this.visible('series').map((x) => ['series', x.id]));
        const total = todo.length;
        let done = 0;
        const stopped = () => !!(shouldStop && shouldStop());
        if (onProgress) onProgress(0, total);
        const worker = async () => {
            while (!stopped() && todo.length) {
                const [kind, id] = todo.shift();
                try {
                    if (kind === 'vod') await this.vodInfo(id);
                    else await this.seriesInfo(id);
                } catch (e) { /* título sin ficha: se ignora */ }
                done++;
                if (onProgress && (done % 5 === 0 || done === total)) onProgress(done, total);
            }
        };
        await Promise.all(Array.from({ length: parallel }, worker));
        const complete = !stopped() && !todo.length;
        if (complete && this.id) {
            const m = storage.get('castScan', {}) || {};
            m[this.id] = Date.now();
            storage.set('castScan', m);
        }
        return complete;
    }

    /** Actores y directores cuyo nombre contiene `query` (todas las palabras), con el número de títulos. */
    async people(query, { limit = 8 } = {}) {
        const q = normalizeText(query);
        if (q.length < 2) return [];
        const terms = q.split(' ');
        const hits = [];
        (await this._peopleIndex()).forEach((p, norm) => {
            if (terms.every((x) => norm.indexOf(x) !== -1)) hits.push({ norm, p });
        });
        hits.sort((a, b) => a.norm.indexOf(q) - b.norm.indexOf(q) || b.p.entries.length - a.p.entries.length);
        const out = [];
        for (const { p } of hits.slice(0, limit * 3)) {
            const keys = new Set(p.entries.filter((e) => this.get(e.kind, e.id)).map((e) => e.kind + ':' + e.id));
            if (!keys.size) continue;
            out.push({ name: p.name, count: keys.size, roles: Array.from(new Set(p.entries.map((e) => e.role))) });
            if (out.length >= limit) break;
        }
        return out;
    }

    _apply(data) {
        this._primary = data;
        this.account = data.account || null;
        this.updatedAt = data.updatedAt || 0;
        this._merge();
        this.loaded = true;
    }

    /**
     * Deja listos los datos precalculados de un catálogo (texto de búsqueda y país de cada título) por trozos, cediendo el hilo
     * a la pantalla: con 60 000 títulos era un bloqueo de más de un segundo en móviles y teles justo al abrir la app.
     * Devuelve cuántos elementos hubo que calcular (0 si venían ya de la caché).
     */
    async _prepareItems(data) {
        if (!data) return 0;
        const cats = data.categories || {};
        let computed = 0;
        for (const kind of ['live', 'vod', 'series']) {
            const names = new Map((cats[kind] || []).map((c) => [c.id, c.name]));
            await sliceEach(data[kind] || [], (item) => { if (prepareItem(item, names.get(item.categoryId))) computed++; });
        }
        return computed;
    }

    /**
     * Ejecuta `fn` cuando la interfaz está tranquila: tras un respiro desde el arranque (la pantalla inicial ya se ha pintado y
     * responde) y, si el navegador lo sabe, en un hueco libre. Así lo pesado de fondo no compite con los primeros toques.
     */
    whenCalm(fn, { minDelay = 1200, timeout = 4000 } = {}) {
        const sinceStart = (typeof performance !== 'undefined' ? performance.now() : 0);
        const wait = Math.max(0, minDelay - sinceStart);
        setTimeout(() => {
            if (typeof requestIdleCallback === 'function') requestIdleCallback(() => fn(), { timeout });
            else fn();
        }, wait);
    }

    /** Proveedores activos para «Mostrar por proveedor»: el principal y los añadidos. Con uno solo no hay nada que elegir. */
    providerChoices({ always = false, kind = '' } = {}) {
        if (!this.profile) return [];
        const extra = (this.extraProfiles ? this.extraProfiles() : []).filter((p) => p && p.id !== this.id);
        // `always`: también con una sola fuente (pantallas vacías: ver cuál es y poder añadir otra).
        if (!extra.length && !always) return [];
        const choice = (p) => ({ id: p.id, name: p.name || '', group: providerGroup(p), icon: providerIcon(p) });
        const list = [choice(this.profile)].concat(extra.map(choice));
        // `kind` ('live' | 'vod' | 'series'): solo los proveedores que aportan ese contenido (en Películas no pintan los de solo canales).
        return kind ? list.filter((p) => this.providerHas(p.id, kind)) : list;
    }

    /** ¿Ese proveedor (el principal o uno añadido) trae algo de ese tipo? Antes de cargar sus datos se da por bueno. */
    providerHas(providerId, kind) {
        if (providerId === this.id) return !this._primary || ((this._primary[kind] || []).length > 0);
        const src = this.sources.get(providerId);
        if (!src) return false;
        return !src.data || ((src.data[kind] || []).length > 0);
    }

    /** ¿El título lo ofrece ese proveedor? Cuenta también como suyo el que otro proveedor repite como alternativa. */
    inProvider(item, providerId) {
        if (!item.source) return providerId === this.id || (item.alt || []).some((a) => a.source === providerId);
        return item.source === providerId || (item.alt || []).some((a) => a.source === providerId);
    }

    /** ¿Se sincroniza lo visto con los servidores propios (Jellyfin, Emby, Plex)? Por defecto sí; depende de recordar la posición. */
    get syncServers() {
        return settings.get('syncServers') !== false && !!settings.get('rememberPosition');
    }

    /**
     * Trae lo que el servidor sabe de lo visto (posición y «visto») y lo adopta como progreso local cuando es más reciente que
     * lo que hay aquí. Así lo empezado en otro dispositivo o en el propio Plex/Jellyfin sale en «Continuar viendo».
     */
    _adoptServerProgress() {
        if (!this.syncServers) return;
        let changed = false;
        this.vod.forEach((item) => {
            if (this._adoptOne('vod', item, item.server, { name: item.name, poster: item.poster })) changed = true;
        });
        if (changed) this._saveProgress().catch(() => {});
    }

    /** Adopta el estado de un título/episodio; devuelve true si cambió el progreso local. */
    _adoptOne(kind, item, s, extra) {
        if (!s) return false;
        const key = this.progressKey(kind, item.id);
        const cur = this.progress[key];
        const at = s.at || 0;
        if (!at) return false; // sin fecha no se sabe qué es más reciente
        if (cur && at <= (cur.updated || 0)) return false;
        const dur = Number(item.durationSecs) || 0;
        if (s.played) {
            if (!cur || cur.finished) return false; // lo visto en el servidor y que aquí no se ha empezado no entra en «continuar»
            this.progress[key] = Object.assign({}, cur, { position: 0, pct: 100, finished: true, updated: at });
            return true;
        }
        if (s.position < 30) return false;
        if (cur && !cur.finished && Math.abs((cur.position || 0) - s.position) < 10) return false; // misma posición: no se toca
        const pct = dur ? Math.min(99, (s.position / dur) * 100) : 0;
        this.progress[key] = Object.assign({}, cur || {}, extra || {}, { position: s.position, duration: dur, pct, finished: false, updated: at });
        return true;
    }

    /**
     * Cuenta al servidor de origen lo que se está viendo (si es Jellyfin, Emby o Plex). Con la reproducción en marcha, como
     * mucho cada 25 s; al parar o terminar, siempre. Nunca molesta: si el servidor no contesta, se ignora.
     */
    reportPlayback(kind, item, { position = 0, duration = 0 } = {}, { stopped = false } = {}) {
        if (!item || !this.syncServers) return;
        const client = this._client(item);
        if (!client || typeof client.report !== 'function') return;
        const key = kind + ':' + item.id;
        const now = Date.now();
        const finished = duration > 0 && position / duration >= 0.96;
        if (!stopped && !finished && now - (this._reportAt.get(key) || 0) < 25000) return;
        this._reportAt.set(key, now);
        Promise.resolve(client.report(this._rawId(item), { position, duration, stopped: stopped || finished, finished })).catch((e) => console.warn('[catalog] aviso al servidor', e && e.message));
    }

    /** Marcas de intro y créditos de un título (solo Jellyfin, Emby y Plex las tienen). Se piden una vez por título y sesión. */
    async segmentsFor(item, durationSecs = 0) {
        if (!item) return [];
        const client = this._client(item);
        if (!client || typeof client.segments !== 'function') return [];
        const key = (item.source || this.id) + '|' + item.id;
        this._segCache = this._segCache || new Map();
        if (!this._segCache.has(key)) this._segCache.set(key, client.segments(this._rawId(item), durationSecs).catch(() => []));
        return this._segCache.get(key);
    }

    /** Proveedores que son «mis bibliotecas»: los servidores y carpetas propios (Jellyfin, Emby, Plex, archivos); no IPTV, listas ni Cine clásico. */
    libraryProviderIds() {
        const out = [];
        if (this.profile && LIBRARY_TYPES.indexOf(this.profile.type) !== -1) out.push(this.id);
        this.sources.forEach((src, id) => { if (src.profile && LIBRARY_TYPES.indexOf(src.profile.type) !== -1) out.push(id); });
        return out;
    }

    /* ---------------- Colecciones, novedades y corregir emparejado de las bibliotecas propias ---------------- */

    /** Colecciones de Jellyfin, Emby y Plex ya resueltas contra el catálogo: [{ key, pid, name, poster, items: [{ kind, item }] }]. */
    collections() {
        return this._collections || [];
    }

    _scheduleCollections() {
        clearTimeout(this._collTimer);
        if (!this.libraryProviderIds().length) {
            if (this._collections && this._collections.length) { this._collections = []; this._collSig = ''; }
            return;
        }
        this._collTimer = setTimeout(() => this.refreshCollections().catch(() => {}), 1500);
        if (this._collTimer && this._collTimer.unref) this._collTimer.unref();
    }

    /** Pide las colecciones a cada servidor (como mucho cada 15 min por proveedor) y las casa con los títulos del catálogo. */
    async refreshCollections({ force = false } = {}) {
        const targets = this.libraryProviderIds()
            .map((pid) => ({ pid, client: pid === this.id ? this.client : (this.sources.get(pid) || {}).client }))
            .filter((x) => x.client && typeof x.client.collections === 'function');
        this._collCache = this._collCache || new Map();
        const index = new Map();
        ['vod', 'series'].forEach((kind) => this[kind].forEach((item) => {
            index.set((item.source || this.id) + '|' + kind + '|' + this._rawId(item), item);
            (item.alt || []).forEach((a) => { if (a.source) index.set(a.source + '|' + kind + '|' + a.id, item); });
        }));
        const out = [];
        for (const { pid, client } of targets) {
            let hit = this._collCache.get(pid);
            if (force || !hit || Date.now() - hit.at > 900000) {
                let list = [];
                try { list = await client.collections(); } catch (e) { list = hit ? hit.list : []; }
                hit = { at: Date.now(), list };
                this._collCache.set(pid, hit);
            }
            hit.list.forEach((c) => {
                const items = [];
                c.ids.forEach((raw) => {
                    const vod = index.get(pid + '|vod|' + raw);
                    const series = vod ? null : index.get(pid + '|series|' + raw);
                    if (vod) items.push({ kind: 'vod', item: vod });
                    else if (series) items.push({ kind: 'series', item: series });
                });
                if (items.length >= 2) out.push({ key: pid + ':' + c.id, pid, name: c.name, poster: c.poster || items[0].item.poster || '', items });
            });
        }
        out.sort((a, b) => b.items.length - a.items.length || a.name.localeCompare(b.name));
        const sig = JSON.stringify(out.map((c) => [c.key, c.items.map((m) => m.item.id)]));
        if (sig === this._collSig) return;
        this._collections = out;
        this._collSig = sig;
        this.emit('change');
    }

    /**
     * Lo añadido a «mis bibliotecas» desde la última vez que se marcó como visto: [{ kind, item }] del más nuevo al más viejo.
     * La primera vez solo fija la marca (sin avisar de todo lo que ya había).
     */
    libraryNews(limit = 24) {
        const ids = this.libraryProviderIds();
        if (!ids.length || !this.loaded) return [];
        const key = 'libNews:' + this.userKey;
        const seen = storage.get(key, null);
        if (seen == null) {
            storage.set(key, Math.floor(Date.now() / 1000));
            return [];
        }
        const rows = [];
        ['vod', 'series'].forEach((kind) => this[kind].forEach((item) => {
            if (item.added > seen && ids.some((id) => this.inProvider(item, id))) rows.push({ kind, item });
        }));
        rows.sort((a, b) => b.item.added - a.item.added);
        return rows.slice(0, limit);
    }

    markLibraryNewsSeen() {
        storage.set('libNews:' + this.userKey, Math.floor(Date.now() / 1000));
        this.emit('change');
    }

    /** ¿Se puede corregir el emparejado de este título? Solo en Jellyfin, Emby y Plex (con permisos de administrador en el servidor). */
    canFixMatch(item) {
        const c = this._client(item);
        return !!(c && typeof c.matchSearch === 'function');
    }

    matchSearch(kind, item, query) {
        return this._client(item).matchSearch(kind, this._rawId(item), query);
    }

    /** Aplica el candidato elegido en el servidor y recarga el catálogo y la ficha. */
    async matchApply(kind, item, candidate) {
        await this._client(item).matchApply(kind, this._rawId(item), candidate);
        const key = this.id + ':' + kind + ':' + item.id;
        this._details.delete(key);
        this._people = null;
        await db.delete('details', key).catch(() => {});
        await this.refresh();
    }

    /** ¿Está activo «Solo mis bibliotecas»? Solo vale si hay alguna biblioteca; si no, se ve todo (no se queda en blanco). */
    get onlyLibraries() {
        return settings.get('onlyLibraries') === true && this.libraryProviderIds().length > 0;
    }

    /** Con «Solo mis bibliotecas», películas y series se limitan a lo que ofrecen esos proveedores (los canales de TV no cambian). */
    _applyOnlyLibraries() {
        if (!this.onlyLibraries) return;
        const ids = this.libraryProviderIds();
        const keep = (item) => ids.some((id) => this.inProvider(item, id));
        ['vod', 'series'].forEach((kind) => {
            this[kind] = this[kind].filter(keep);
            const used = new Set(this[kind].map((m) => String(m.categoryId)));
            this.categories[kind] = this.categories[kind].filter((c) => used.has(String(c.id)));
        });
    }

    /** Rehace el catálogo que se ve tras cambiar «Solo mis bibliotecas» y avisa a las pantallas. */
    setOnlyLibraries(on) {
        settings.set('onlyLibraries', !!on);
        if (this._primary) {
            this._merge();
            this.emit('change');
        }
    }

    _blockFilter(profile, data) {
        const preset = profile ? presetOf(profile) : null;
        return withoutBlocked(data, { listId: preset && profile.type === 'm3u' ? preset.id : '', isArchive: !!profile && profile.type === 'archive' });
    }

    /** Catálogo que se ve: el del principal más los proveedores añadidos que ya han cargado. */
    _merge() {
        // Lo denunciado y retirado (core/content-block.js) no se muestra, aunque esté en la caché del proveedor.
        const data = this._blockFilter(this.profile, this._primary || {});
        const extras = [];
        this.sources.forEach((src, id) => { if (src.data) extras.push({ id, data: this._blockFilter(src.profile, src.data) }); });
        const cats = data.categories || {};
        const m = extras.length
            ? mergeCatalogs(data, extras)
            : { live: data.live || [], vod: data.vod || [], series: data.series || [], categories: { live: cats.live || [], vod: cats.vod || [], series: cats.series || [] }, stats: {} };
        this._uidIndex = null;
        this._recommended = null;
        this._trendingMatch = null;
        this.live = m.live;
        this.vod = m.vod;
        this.series = m.series;
        this.categories = { live: m.categories.live, vod: m.categories.vod, series: m.categories.series };
        this._applyOnlyLibraries();
        this.mergeStats = m.stats;
        this._buildIndexes();
        this._adoptServerProgress();
        this._scheduleCollections();
    }

    _buildIndexes() {
        this._titleIdx = null;
        this.countries = { live: [], vod: [], series: [] };
        this._catCountries = { live: new Map(), vod: new Map(), series: new Map() };
        ['live', 'vod', 'series'].forEach((kind) => {
            const byId = new Map();
            const byCat = new Map();
            const catName = new Map();
            (this.categories[kind] || []).forEach((c) => catName.set(c.id, c.name));
            const catCountries = this._catCountries[kind];
            this[kind].forEach((item) => {
                // Texto de búsqueda y país de emisión (no el de producción, que es `country`) según el prefijo del nombre («FR| …») o el de su
                // categoría («ES - DEPORTES»). Se guardan con el elemento (también en la caché): si ya están y no han cambiado, no se recalculan.
                prepareItem(item, catName.get(item.categoryId));
                const seen = catCountries.get(item.categoryId);
                if (seen) seen.add(item.region); else catCountries.set(item.categoryId, new Set([item.region]));
                byId.set(item.id, item);
                const list = byCat.get(item.categoryId);
                if (list) list.push(item); else byCat.set(item.categoryId, [item]);
            });
            this._index[kind] = byId;
            this._catIndex[kind] = byCat;
            this.countries[kind] = countCountries(this[kind]);
            this.categories[kind] = (this.categories[kind] || [])
                .map((c) => Object.assign({}, c, { count: (byCat.get(c.id) || []).length, adult: isAdultCategoryName(c.name) }))
                .filter((c) => c.count > 0);
        });
    }

    async loadEpg({ force = false } = {}) {
        const url = this._epgUrlFor(this.profile, this.client, this._primary);
        if (!url) return false;
        return epg.load(this.id, url, { force });
    }

    /* ---------------- Consultas ---------------- */

    get(kind, id) {
        return this._index[kind].get(String(id)) || null;
    }

    /** Variantes del mismo canal en distintas calidades (La 1 SD/HD/FHD) del mismo proveedor, de mejor a peor. Solo el canal si no hay más. */
    channelVariants(id) {
        const ch = this.get('live', id);
        if (!ch) return [];
        const key = variantKey(ch.name);
        if (!key) return [ch];
        const src = ch.source || '';
        return sortVariants(this.visibleRef('live').filter((c) => (c.source || '') === src && variantKey(c.name) === key));
    }

    category(kind, id) {
        // Se consulta por cada título al filtrar (control parental): con un mapa en vez de recorrer la lista cada vez.
        const arr = this.categories[kind] || [];
        let m = this._catById && this._catById[kind];
        if (!m || m.src !== arr || m.n !== arr.length) {
            m = { src: arr, n: arr.length, map: new Map(arr.map((c) => [String(c.id), c])) };
            if (!this._catById) this._catById = {};
            this._catById[kind] = m;
        }
        return m.map.get(String(id)) || null;
    }

    byCategory(kind, categoryId) {
        if (!categoryId || categoryId === 'all') return this.visible(kind);
        return this.filterVisible(kind, this._catIndex[kind].get(String(categoryId)) || []);
    }

    /**
     * Todo lo visible de un tipo. Se pinta el inicio llamando varias veces a esto y con catálogos de 100 000 títulos
     * cada recorrido eran decenas de ms (segundos en una tele): el resultado se guarda mientras no cambie nada de lo
     * que lo decide (lista, ocultos, bloqueo parental, perfil infantil) y se entrega una copia, porque quien llama ordena.
     */
    visible(kind) {
        return this.visibleRef(kind).slice();
    }

    /** Igual que `visible` pero sin copiar: la misma lista mientras nada cambie (sirve para memorizar cálculos). NO modificar. */
    visibleRef(kind) {
        const list = this[kind];
        const sig = (this.lockActive ? 'L' : '-') + (this.isKids ? 'K' : '-') + '|' + this.hidden.size + '|' + JSON.stringify(settings.get('parental') || null);
        const c = this._visCache && this._visCache[kind];
        if (c && c.list === list && c.len === list.length && c.hidden === this.hidden && c.cats === this.categories[kind] && c.sig === sig) return c.out;
        const out = this.filterVisible(kind, list);
        if (!this._visCache) this._visCache = {};
        this._visCache[kind] = { list, len: list.length, hidden: this.hidden, cats: this.categories[kind], sig, out };
        return out;
    }

    /** Unión de varias categorías (ids); sin ids, todo el catálogo visible. Conserva el orden del proveedor. */
    /** Países de los que una categoría tiene algo ('' = sin país). */
    categoryCountries(kind, id) {
        const set = this._catCountries && this._catCountries[kind] && this._catCountries[kind].get(id);
        return set ? Array.from(set) : [];
    }

    byCategories(kind, ids) {
        if (!ids || !ids.length) return this.visible(kind);
        if (ids.length === 1) return this.byCategory(kind, ids[0]);
        const wanted = new Set(ids.map(String));
        return this.filterVisible(kind, this[kind].filter((it) => wanted.has(String(it.categoryId))));
    }

    filterVisible(kind, items) {
        const lockActive = this.lockActive;
        const kids = this.isKids;
        // Con el control parental activo (lo está por defecto), las categorías bloqueadas se resuelven UNA vez por tipo, no título a
        // título: con 60 000 títulos era la parte más cara de cada filtrado.
        const locked = lockActive ? this.lockedCategorySet(kind) : null;
        const hidden = this.hidden;
        const prefix = kind + ':';
        return items.filter((item) => {
            if (hidden.size && hidden.has(prefix + item.id)) return false;
            if (locked && locked.has(String(item.categoryId))) return false;
            // Perfil infantil: además de la categoría, se descarta cualquier elemento cuyo propio
            // nombre delate contenido adulto (p. ej. "+18 • Adult 27" en una categoría genérica).
            if (kids && isAdultCategoryName(item.name)) return false;
            return true;
        });
    }

    /** Ids (como texto) de las categorías bloqueadas de un tipo: lo que dice isLockedCategory, calculado para todas de una vez. */
    lockedCategorySet(kind) {
        const set = new Set();
        (this.categories[kind] || []).forEach((c) => { if (this.isLockedCategory(kind, c.id)) set.add(String(c.id)); });
        // Categorías marcadas a mano que ya no están en la lista del proveedor (sus títulos huérfanos siguen bloqueados).
        const custom = (settings.get('parental') || {}).categories || {};
        Object.keys(custom).forEach((k) => { if (custom[k] === true && k.indexOf(prefixOf(kind)) === 0) set.add(k.slice(prefixOf(kind).length)); });
        return set;
    }

    search(query, { limit = 40 } = {}) {
        const q = normalizeText(query);
        if (q.length < 2) return { live: [], vod: [], series: [] };
        const terms = q.split(' ');
        const match = (item) => terms.every((t) => item._s.indexOf(t) !== -1);
        const scored = (items) => {
            const out = [];
            for (let i = 0; i < items.length && out.length < limit * 3; i++) {
                if (match(items[i])) out.push(items[i]);
            }
            out.sort((a, b) => a._s.indexOf(q) - b._s.indexOf(q) || a.name.length - b.name.length);
            return out.slice(0, limit);
        };
        return {
            live: scored(this.visible('live')),
            vod: scored(this.visible('vod')),
            series: scored(this.visible('series'))
        };
    }

    /* ---------------- Favoritos / ocultos / recientes ---------------- */

    isFavorite(kind, id) {
        return this.favorites.has(kind + ':' + id);
    }

    async toggleFavorite(kind, id) {
        const key = kind + ':' + id;
        const added = !this.favorites.has(key);
        if (added) this.favorites.add(key); else this.favorites.delete(key);
        await db.put('favorites', this.userKey + ':favorites', Array.from(this.favorites));
        this._push('favorites');
        this.emit('favorites', { kind, id, added });
        this.emit('change');
        return added;
    }

    favoriteItems(kind) {
        const out = [];
        this.favorites.forEach((key) => {
            const [k, id] = key.split(':');
            if (k !== kind) return;
            const item = this.get(kind, id);
            if (item) out.push(item);
        });
        return this.filterVisible(kind, out);
    }

    async hide(kind, id) {
        this.hidden.add(kind + ':' + id);
        await db.put('favorites', this.userKey + ':hidden', Array.from(this.hidden));
        this._push('hidden');
        this.emit('change');
    }

    async touchRecent(channelId) {
        this.recent = [String(channelId)].concat(this.recent.filter((id) => id !== String(channelId))).slice(0, MAX_RECENT);
        await db.put('meta', this.userKey + ':recent', this.recent);
        this._push('recent');
        this.emit('change');
    }

    recentChannels() {
        return this.filterVisible('live', this.recent.map((id) => this.get('live', id)).filter(Boolean));
    }

    /**
     * Función título → «cuándo lo viste por última vez» (0 si nunca) para ordenar con lo más reciente primero.
     * Canales: su puesto en los vistos hace poco; películas y episodios: la marca de tiempo del progreso.
     */
    watchedAt(kind) {
        if (kind === 'live') {
            const rank = new Map();
            this.recent.forEach((id, i) => { rank.set(String(id), this.recent.length - i); });
            return (item) => rank.get(String(item.id)) || 0;
        }
        const last = new Map();
        Object.keys(this.progress).forEach((key) => {
            const p = this.progress[key];
            if (!p || !p.updated) return;
            const id = kind === 'vod' && key.indexOf('vod:') === 0 ? key.slice(4) : kind === 'series' && key.indexOf('episode:') === 0 && p.seriesId ? String(p.seriesId) : '';
            if (id && p.updated > (last.get(id) || 0)) last.set(id, p.updated);
        });
        return (item) => last.get(String(item.id)) || 0;
    }

    /* ---------------- Progreso ---------------- */

    progressKey(kind, id) {
        return kind + ':' + id;
    }

    getProgress(kind, id) {
        return this.progress[this.progressKey(kind, id)] || null;
    }

    /** Guarda la posición. kind: 'vod' | 'episode'. */
    async setProgress(kind, id, data) {
        if (!settings.get('rememberPosition')) return;
        const key = this.progressKey(kind, id);
        const pct = data.duration ? (data.position / data.duration) * 100 : 0;
        if (pct >= 96 || (data.position < 30 && pct < 2)) {
            // Terminado (o apenas empezado): fuera de "continuar viendo".
            if (this.progress[key]) {
                // No se borra la entrada: con posición 0 desaparece de "continuar viendo" y, al
                // sincronizar entre dispositivos, la marca de tiempo impide que reviva.
                this.progress[key] = Object.assign({}, this.progress[key], pct >= 96
                    ? { position: 0, pct: 100, finished: true, updated: Date.now() }
                    : { position: 0, pct: 0, finished: false, updated: Date.now() });
                await this._saveProgress();
            }
            return;
        }
        this.progress[key] = Object.assign({}, this.progress[key] || {}, data, { pct, finished: false, updated: Date.now() });
        await this._saveProgress();
    }

    async removeProgress(kind, id) {
        const key = this.progressKey(kind, id);
        if (this.progress[key]) this.progress[key] = Object.assign({}, this.progress[key], { position: 0, pct: 0, finished: false, updated: Date.now() });
        await this._saveProgress();
    }

    async _saveProgress() {
        await db.put('progress', this.userKey, this.progress);
        this._push('progress');
        this.emit('progress');
        this.emit('change');
    }

    /* ---------------- Visto / no visto (como en Plex) ---------------- */

    isWatched(kind, id) {
        const p = this.getProgress(kind, id);
        return !!(p && p.finished);
    }

    /**
     * Marca una película ('vod') o un episodio ('episode') como visto o no visto. Es el mismo registro que el progreso
     * de reproducción (se sincroniza igual, gana el más reciente): visto = terminado, no visto = empezar de cero.
     * Para un episodio `extra` lleva { seriesId, season, episode, title, poster }.
     */
    async setWatched(kind, id, on, extra = {}) {
        this._markWatched(kind, id, on, extra, Date.now());
        await this._saveProgress();
    }

    /**
     * Toda una temporada de golpe (`episodes` = los de la ficha). Cada episodio lleva una marca un milisegundo posterior a
     * la anterior: así el «último visto» de la serie es el último episodio de la temporada y «Siguiente» propone el que sigue.
     */
    async setEpisodesWatched(seriesId, episodes, on, poster = '') {
        const base = Date.now();
        episodes.forEach((ep, i) => this._markWatched('episode', ep.id, on, { seriesId: String(seriesId), season: ep.season, episode: ep.num, title: ep.title, poster }, base + i));
        await this._saveProgress();
    }

    _markWatched(kind, id, on, extra, at) {
        const key = this.progressKey(kind, id);
        const cur = this.progress[key] || {};
        this.progress[key] = Object.assign({}, cur, extra, on ? { position: 0, pct: 100, finished: true } : { position: 0, pct: 0, finished: false }, { updated: at });
    }

    /** Elementos para "Continuar viendo": películas y el último episodio visto de cada serie. */
    continueWatching(limit = 20) {
        const rows = [];
        const seenSeries = new Set();
        Object.keys(this.progress)
            .map((key) => Object.assign({ key }, this.progress[key]))
            .filter((p) => !p.finished && p.pct > 0)
            .sort((a, b) => b.updated - a.updated)
            .forEach((p) => {
                const [kind, id] = p.key.split(':');
                if (kind === 'vod') {
                    const item = this.get('vod', id);
                    if (item && !this.hidden.has('vod:' + id)) rows.push({ kind: 'vod', item, progress: p });
                } else if (kind === 'episode') {
                    if (seenSeries.has(p.seriesId)) return;
                    const series = this.get('series', p.seriesId);
                    if (series && !this.hidden.has('series:' + p.seriesId)) {
                        seenSeries.add(p.seriesId);
                        rows.push({ kind: 'episode', item: series, progress: p });
                    }
                }
            });
        return rows.slice(0, limit);
    }

    /** Quita un título de "Continuar viendo": la película, o todos los episodios a medias de la serie. */
    async removeFromContinue(kind, id) {
        if (kind === 'vod') return this.removeProgress('vod', id);
        let changed = false;
        Object.keys(this.progress).forEach((key) => {
            const p = this.progress[key];
            if (key.indexOf('episode:') !== 0 || p.seriesId !== String(id) || p.finished || !(p.pct > 0)) return;
            this.progress[key] = Object.assign({}, p, { position: 0, pct: 0, finished: false, updated: Date.now() });
            changed = true;
        });
        if (changed) await this._saveProgress();
        return undefined;
    }

    /**
     * "Recomendado para ti": películas y series sin empezar afines a lo que el espectador ha visto
     * (más peso si pasó de la mitad o lo terminó) y a lo que tiene en Mi lista. Sin historial, vacío.
     * El resultado se conserva mientras no cambien el historial, la lista ni el catálogo.
     */
    /** Lo visto, empezado o guardado, con cuándo y cuánto: la base de «Recomendado para ti» y «Porque viste X». */
    _tasteSeeds() {
        const seeds = [];
        const byKey = new Map();
        const add = (kind, id, patch) => {
            const key = kind + ':' + id;
            const item = this.get(kind, id);
            if (!item) return;
            let s = byKey.get(key);
            if (!s) { s = { key, kind, item, at: 0, pct: 0, finished: false, fav: false }; byKey.set(key, s); seeds.push(s); }
            if (patch.at > s.at) s.at = patch.at;
            if (patch.pct > s.pct) s.pct = patch.pct;
            if (patch.finished) s.finished = true;
            if (patch.fav) s.fav = true;
        };
        Object.keys(this.progress).forEach((key) => {
            const p = this.progress[key];
            if (!p || !(p.finished || p.pct > 0)) return;
            const patch = { at: Number(p.updated) || 0, pct: Number(p.pct) || 0, finished: !!p.finished };
            if (key.indexOf('vod:') === 0) add('vod', key.slice(4), patch);
            else if (key.indexOf('episode:') === 0 && p.seriesId) add('series', p.seriesId, patch);
        });
        const marks = this.marks || {};
        this.favorites.forEach((key) => {
            const i = key.indexOf(':');
            const kind = key.slice(0, i);
            if (kind === 'vod' || kind === 'series') add(kind, key.slice(i + 1), { at: Number(marks['fav:' + key]) || Date.now(), pct: 0, finished: false, fav: true });
        });
        return seeds;
    }

    /** Director, reparto y géneros de las fichas ya abiertas (TMDB): afinan lo recomendado. Se anotan al abrir una ficha y, para lo ya visto, al cargar. */
    _noteFeatures(kind, id, data) {
        if (!data) return;
        const cast = splitCast(data.cast).slice(0, 6);
        const director = splitCast(data.director);
        if (!data.genre && !cast.length && !director.length) return;
        if (!this._feat) this._feat = new Map();
        this._feat.set(kind + ':' + id, { genre: data.genre || '', cast, director });
    }

    _featOf(kind, item) {
        return (this._feat && this._feat.get(kind + ':' + item.id)) || null;
    }

    /** Lee de la caché las fichas de lo visto que aún no tienen sus características (pocas decenas), y avisa para repintar. */
    _loadSeedFeatures(seeds) {
        if (!this.id || this._featLoading) return;
        const missing = seeds.filter((s) => !(this._feat && this._feat.has(s.key)) && !(this._featTried && this._featTried.has(s.key)));
        if (!missing.length) return;
        this._featLoading = true;
        if (!this._featTried) this._featTried = new Set();
        const pid = this.id;
        Promise.all(missing.slice(0, 60).map(async (s) => {
            this._featTried.add(s.key);
            try {
                const row = await db.get('details', pid + ':' + s.kind + ':' + s.item.id);
                if (row && row.data) this._noteFeatures(s.kind, s.item.id, row.data);
            } catch (e) { /* sin ficha guardada */ }
        })).then(() => {
            this._featLoading = false;
            this._recommended = null;
            this._because = null;
            if (this._feat && this._feat.size) this.emit('change');
        }).catch(() => { this._featLoading = false; });
    }

    _recoCandidates(seen) {
        const candidates = [];
        ['vod', 'series'].forEach((kind) => {
            this.visibleRef(kind).forEach((item) => {
                if (!seen.has(kind + ':' + item.id)) candidates.push({ kind, item });
            });
        });
        return candidates;
    }

    _tasteSignature(seeds, limit) {
        return [this.updatedAt, this.vod.length, this.series.length, this.hidden.size, this.lockActive ? 1 : 0, limit, this._feat ? this._feat.size : 0,
            seeds.map((s) => s.key + '*' + s.pct + (s.finished ? 'f' : '') + (s.fav ? 'v' : '') + '@' + Math.floor(s.at / 86400000)).sort().join(',')].join('|');
    }

    /**
     * Las dos filas de gustos («Recomendado para ti» y «Porque viste X») para pintar el Inicio SIN bloquearlo: si ya están
     * calculadas para lo visto ahora, se devuelven; si no, se devuelven las anteriores (o nada) y se calculan después, en un momento
     * tranquilo y por trozos, y al terminar se avisa para repintar. Con catálogos de 100 000 títulos el cálculo cuesta segundos.
     */
    tasteRows(limit = 24, rows = 2) {
        const empty = NO_TASTE;
        const seeds = this._tasteSeeds();
        if (!seeds.length) return empty;
        this._loadSeedFeatures(seeds);
        const signature = this._tasteSignature(seeds, limit) + '|' + rows;
        if (this._taste && this._taste.signature === signature) return this._taste;
        this._scheduleTaste(limit, rows);
        return this._taste || empty;
    }

    _scheduleTaste(limit, rows) {
        if (this._tasteTimer) return;
        this._tasteTimer = true;
        const run = async () => {
            try {
                // Primero se rellena (por trozos, cediendo la pantalla) la caché de géneros de todo el catálogo; luego se calcula.
                for (const kind of ['vod', 'series']) await sliceEach(this[kind] || [], (item) => warmFeatures(kind, item));
                await yieldToUi();
                const recommended = this.recommended(limit);
                await yieldToUi();
                const because = this.becauseYouWatched({ rows });
                const seeds = this._tasteSeeds();
                this._taste = { signature: this._tasteSignature(seeds, limit) + '|' + rows, recommended, because };
                this.emit('change');
            } catch (e) { /* sin recomendaciones por ahora */ } finally {
                this._tasteTimer = false;
            }
        };
        this._idle(run);
    }

    /** Ejecuta `fn` en un momento tranquilo: unos segundos después de lo que se esté haciendo y, si se puede, en un hueco libre. */
    _idle(fn, delay = 2500) {
        setTimeout(() => {
            if (typeof requestIdleCallback === 'function') requestIdleCallback(() => fn(), { timeout: 8000 });
            else fn();
        }, delay);
    }

    recommended(limit = 24) {
        const seeds = this._tasteSeeds();
        if (!seeds.length) return [];
        this._loadSeedFeatures(seeds);
        const signature = this._tasteSignature(seeds, limit);
        if (this._recommended && this._recommended.signature === signature) return this._recommended.list;
        const seen = new Set(seeds.map((s) => s.key));
        const list = recommend({ seeds, candidates: this._recoCandidates(seen), limit, featOf: (k, i) => this._featOf(k, i), now: Date.now() });
        this._recommended = { signature, list };
        return list;
    }

    /**
     * «Porque viste X»: filas con los títulos más parecidos a lo último que se vio entero (o casi), por director, reparto,
     * géneros, categoría y saga. → [{ kind, seed, items: [{ kind, item, score }] }]
     */
    becauseYouWatched({ rows = 2, perRow = 20 } = {}) {
        const seeds = this._tasteSeeds();
        if (!seeds.length) return [];
        this._loadSeedFeatures(seeds);
        const signature = this._tasteSignature(seeds, rows * 100 + perRow);
        if (this._because && this._because.signature === signature) return this._because.list;
        const seen = new Set(seeds.map((s) => s.key));
        const list = becauseRows({ seeds, candidates: this._recoCandidates(seen), seen, rows, perRow, featOf: (k, i) => this._featOf(k, i), now: Date.now() });
        this._because = { signature, list };
        return list;
    }

    /**
     * «Más como esto» de una ficha: títulos del mismo tipo ordenados por lo parecidos que son a este (género, categoría, director,
     * reparto, saga) y por lo que le gusta al espectador (lo que ha visto y guardado); lo ya visto va detrás de lo nuevo y la
     * valoración desempata. Sin historial se queda en el parecido con este título. Se calcula sobre su categoría (o, si es
     * pequeña, sobre lo que comparte género) y se recuerda mientras no cambien el historial ni el catálogo.
     */
    similarTo(kind, item, limit = 20) {
        if (!item) return [];
        const seeds = this._tasteSeeds();
        const signature = this._tasteSignature(seeds, 7) + '|' + (this.visibleRef ? this.visibleRef(kind).length : 0);
        const key = kind + ':' + item.id;
        if (!this._similar) this._similar = new Map();
        const hit = this._similar.get(key);
        if (hit && hit.signature === signature && hit.limit >= limit) return hit.list.slice(0, limit);
        const id = String(item.id);
        let pool = this.byCategory(kind, item.categoryId).filter((m) => String(m.id) !== id);
        if (pool.length > 2500) pool = pool.slice(0, 2500);
        if (pool.length < 40) {
            // Categoría pequeña: se amplía con lo que comparte género con este título.
            const g = genreTokens(item.genre || (this._featOf(kind, item) || {}).genre);
            if (g.length) {
                const have = new Set(pool.map((m) => String(m.id)));
                const all = this.visible(kind);
                for (let i = 0; i < all.length && pool.length < 1500; i++) {
                    const m = all[i];
                    if (String(m.id) === id || have.has(String(m.id))) continue;
                    if (genreTokens(m.genre).some((x) => g.indexOf(x) !== -1)) { pool.push(m); have.add(String(m.id)); }
                }
            }
        }
        if (!pool.length) return [];
        const featOf = (k, i) => this._featOf(k, i);
        const cur = { kind, item };
        const taste = new Map();
        let maxTaste = 0;
        if (seeds.length) {
            this._loadSeedFeatures(seeds);
            recommend({ seeds, candidates: pool.map((m) => ({ kind, item: m })), limit: pool.length, maxPerCategory: pool.length, featOf, now: Date.now() })
                .forEach((r) => { taste.set(String(r.item.id), r.score); if (r.score > maxTaste) maxTaste = r.score; });
        }
        const seenKeys = new Set(seeds.filter((s) => s.finished || s.pct >= 50).map((s) => s.key));
        const scored = pool.map((m) => {
            const sim = similarity(cur, { kind, item: m }, featOf);
            const like = maxTaste ? (taste.get(String(m.id)) || 0) / maxTaste : 0;
            const rating = Math.min(10, Number(m.rating) || 0) / 10;
            return { item: m, score: sim + like * 0.8 + rating * 0.15 - (seenKeys.has(kind + ':' + m.id) ? 1.5 : 0) };
        });
        scored.sort((a, b) => b.score - a.score || (b.item.added || 0) - (a.item.added || 0));
        const list = scored.slice(0, Math.max(limit, 20)).map((x) => x.item);
        this._similar.set(key, { signature, limit: Math.max(limit, 20), list });
        if (this._similar.size > 60) this._similar.delete(this._similar.keys().next().value);
        return list.slice(0, limit);
    }

    /**
     * Carga las tendencias de la semana (TMDB, a través del servidor) como mucho cada 6 horas.
     * `trendingSource` lo pone main.js si el servidor tiene clave de TMDB; sin ella no hay fila.
     */
    async loadTrending() {
        if (!this.trendingSource || this._trendingLoading) return;
        if (this._trending && Date.now() - this._trending.time < TRENDING_TTL) return;
        this._trendingLoading = true;
        try {
            const cached = await db.get('meta', 'trending');
            if (cached && cached.list && !this._trending) {
                this._trending = cached;
                this.emit('change');
            }
            if (!(cached && Date.now() - cached.time < TRENDING_TTL) && navigator.onLine !== false) {
                const list = await this.trendingSource();
                if (list && list.length) {
                    this._trending = { time: Date.now(), list };
                    await db.put('meta', 'trending', this._trending);
                    this.emit('change');
                }
            }
        } catch (e) {
            console.warn('[catalog] tendencias no disponibles', e);
        } finally {
            this._trendingLoading = false;
        }
    }

    /** "Tendencias": lo más visto de la semana que está en el catálogo de este proveedor. */
    trending(limit = 20) {
        if (!this._trending || !this.loaded) return [];
        const signature = [this._trending.time, this.updatedAt, this.vod.length, this.series.length, this.hidden.size, this.lockActive ? 1 : 0, this.isKids ? 1 : 0, limit].join('|');
        if (this._trendingMatch && this._trendingMatch.signature === signature) return this._trendingMatch.list;
        const list = matchTrending(this._trending.list, { vod: this.visible('vod'), series: this.visible('series') }, { limit });
        this._trendingMatch = { signature, list };
        return list;
    }

    /* ---------------- Top 10 (contador propio o tendencias) ---------------- */

    /**
     * Cuenta que se ha visto un título para el «Top 10» del servidor. `kind` = 'vod' | 'series' (de una serie se pasa la serie, no el
     * episodio). Cuenta cuando llevas más de un minuto, una vez por título cada 12 h en este aparato. `playsSink` lo pone main.js
     * solo con cuenta; sin él (apps sin servidor) no se hace nada.
     */
    countPlay(kind, item, position = 0) {
        if (!this.playsSink || !item || (kind !== 'vod' && kind !== 'series') || position < 60) return;
        const key = titleKey(item.name);
        if (key.length < 2) return;
        this._countedAt = this._countedAt || new Map();
        const mark = kind + ':' + key;
        const now = Date.now();
        if (now - (this._countedAt.get(mark) || 0) < 12 * 3600 * 1000) return;
        this._countedAt.set(mark, now);
        Promise.resolve(this.playsSink({ kind, key })).catch((e) => console.warn('[catalog] aviso para el Top 10', e && e.message));
    }

    /** Pide el ranking de la semana al servidor (como mucho cada 30 min; se guarda para el siguiente arranque). */
    async loadTop() {
        if (!this.topSource || this._topLoading) return;
        if (this._top && Date.now() - this._top.time < TOP_TTL) return;
        this._topLoading = true;
        try {
            const cached = await db.get('meta', 'top');
            if (cached && cached.list && !this._top) {
                this._top = cached;
                this.emit('change');
            }
            if (!(cached && Date.now() - cached.time < TOP_TTL) && navigator.onLine !== false) {
                const list = await this.topSource();
                if (Array.isArray(list)) {
                    this._top = { time: Date.now(), list };
                    await db.put('meta', 'top', this._top);
                    this.emit('change');
                }
            }
        } catch (e) {
            console.warn('[catalog] Top 10 no disponible', e);
        } finally {
            this._topLoading = false;
        }
    }

    /**
     * «Top 10»: de más a menos visto, solo con lo que hay en el catálogo. `source`: 'own' (lo que se ve en este servidor; si faltan
     * títulos para llegar a `limit`, se completa con las tendencias de TMDB para que la fila no quede coja), 'trending' (solo
     * TMDB) u 'off'. Devuelve { source, entries: [{ kind, item, rank }] }; `source` dice de dónde sale de verdad (para el título).
     */
    topTen(limit = 10, source = 'own') {
        if (!this.loaded || source === 'off') return { source: 'off', entries: [] };
        const signature = [source, limit, this._top && this._top.time, this._trending && this._trending.time, this.updatedAt, this.vod.length, this.series.length, this.hidden.size, this.lockActive ? 1 : 0, this.isKids ? 1 : 0].join('|');
        if (this._topMatch && this._topMatch.signature === signature) return this._topMatch.value;
        let own = [];
        if (source === 'own' && this._top && this._top.list) {
            const ranked = this._top.list.map((e) => ({ kind: e.kind, title: e.key }));
            own = matchTrending(ranked, { vod: this.visible('vod'), series: this.visible('series') }, { limit });
        }
        let entries = own;
        let from = own.length ? 'own' : 'trending';
        if (own.length < limit) {
            const seen = new Set(own.map((r) => r.kind + ':' + r.item.id));
            this.trending(limit * 2).forEach((r) => { if (entries.length < limit && !seen.has(r.kind + ':' + r.item.id)) entries = entries.concat([r]); });
        }
        if (source === 'trending') { entries = this.trending(limit); from = 'trending'; }
        const value = { source: from, own: own.length, entries: entries.slice(0, limit).map((r, i) => ({ kind: r.kind, item: r.item, rank: i + 1 })) };
        this._topMatch = { signature, value };
        return value;
    }

    /** Último progreso de una serie (para "Continuar T2 E3"). */
    seriesProgress(seriesId) {
        let best = null;
        Object.keys(this.progress).forEach((key) => {
            const p = this.progress[key];
            if (key.indexOf('episode:') === 0 && p.seriesId === String(seriesId)) {
                if (!best || p.updated > best.updated) best = p;
            }
        });
        return best;
    }

    /* ---------------- Detalles ---------------- */

    /**
     * Completa una ficha con TMDB cuando el panel no trae reparto, director, sinopsis, fondo,
     * valoración o edad. `enricher` lo pone main.js (account.tmdbLookup) si el servidor tiene clave.
     * Marca `enriched` para no volver a consultar; si la consulta falla, se reintentará otro día.
     */
    async _enrich(kind, info, base = info) {
        // `enriched: 4` = consultada con la versión que también trae compositor y título original (3: fotos del reparto, tráiler y dónde verla) (las anteriores se repiten una vez).
        if (!this.enricher || !info || info.enriched === 4) return info;
        const policy = this._tmdbPolicy(base);
        // Ajuste «Fichas con TMDB» → «solo si faltan datos básicos»: lo que ya trae la biblioteca (Jellyfin, Emby, Plex…) basta.
        if (settings.get('tmdbMode') === 'basics' && this._hasBasics(info)) return Object.assign({}, info, { enriched: 4, tmdbOk: 1, composer: info.composer || '' });
        if (policy === 'skip') return Object.assign({}, info, { enriched: 4, tmdbOk: 1, composer: info.composer || '' });
        const photos = Array.isArray(info.castList) && info.castList.length > 0;
        const missing = !photos || !info.youtube || info.watch === undefined || info.composer === undefined || !info.cast || !info.director || !info.plot || !info.backdrop || !info.age || !info.rating || (kind === 'vod' && !info.durationSecs);
        if (!missing) return Object.assign({}, info, { enriched: 4, tmdbOk: 1 });
        let x;
        try {
            x = await this.enricher(kind, info);
        } catch (e) {
            return info;
        }
        if (!x) return info;
        if (!x.tmdbId || (policy === 'strict' && !confidentMatch(info, x))) return Object.assign({}, info, { enriched: 4, tmdbOk: 1, composer: info.composer || '' });
        const castList = Array.isArray(x.cast) ? x.cast : [];
        return Object.assign({}, info, {
            enriched: 4,
            tmdbOk: 1,
            srcTmdb: 1,
            tmdb: info.tmdb || x.tmdbId,
            imdb: info.imdb || x.imdbId || '',
            cast: info.cast || castList.map((c) => c.name).join(', '),
            castList: castList.length ? castList : info.castList,
            director: info.director || x.director || '',
            composer: info.composer || x.composer || '',
            originalTitle: info.originalTitle || x.originalTitle || '',
            plot: info.plot || x.overview || '',
            backdrop: info.backdrop || x.backdrop || '',
            poster: info.poster || x.poster || '',
            watch: x.watch || info.watch || {},
            tmdbTitle: info.tmdbTitle || x.title || '',
            youtube: info.youtube || (x.trailer ? 'https://www.youtube.com/watch?v=' + x.trailer : ''),
            age: info.age || x.certification || '',
            rating: info.rating || x.rating || 0,
            genre: info.genre || x.genres || '',
            year: info.year || x.year || '',
            durationSecs: info.durationSecs || (kind === 'vod' && x.runtime ? x.runtime * 60 : 0),
            episodeRunTime: info.episodeRunTime || (kind === 'series' && x.runtime ? String(x.runtime) : '')
        });
    }

    /** La ficha de un título, con lo que se haya corregido a mano (item-edits.js) puesto encima. */
    async _detailInfo(kind, id) {
        const data = await this._detailInfoRaw(kind, id);
        if (this.editApply && data) this.editApply(kind, id, data);
        return data;
    }

    async _detailInfoRaw(kind, id) {
        const base = this.get(kind, id);
        if (!base) return null;
        const key = this.id + ':' + kind + ':' + id;
        const mem = this._details.get(key);
        // «No usar TMDB»: lo que ya se había completado con TMDB (en memoria o guardado) tampoco se enseña; la ficha se rehace con lo que da la fuente.
        const tmdbOff = this._tmdbOff();
        if (mem && !(tmdbOff && mem.enriched) && (mem.enriched === 4 || !this.enricher)) return mem;
        const cached = await db.get('details', key);
        const fresh = cached && Date.now() - cached.updated < DETAILS_TTL;
        // Las fichas de carpetas propias guardadas antes de que se comprobara el parecido con TMDB se rehacen sin lo que TMDB puso.
        const needOmdb = !!(this.omdbSource && cached && cached.data && !cached.data.omdbOk); // se acaba de activar OMDb
        const oldTmdb = needOmdb || (this._tmdbPolicy(base) !== 'free' && cached && cached.data && cached.data.enriched && !cached.data.tmdbOk) || (tmdbOff && cached && cached.data && cached.data.enriched);
        let data = fresh && !oldTmdb ? cached.data : null;
        if (!data) {
            const client = this._client(base);
            if (client) {
                try {
                    data = this._tagEpisodes(await this._clientInfo(client, kind, this._rawId(base), base), base);
                } catch (err) {
                    if (cached) data = cached.data;
                    else return kind === 'vod' ? base : Object.assign({ seasons: [] }, base);
                }
            } else {
                data = kind === 'vod' ? Object.assign({}, base) : Object.assign({ seasons: [] }, base);
            }
        }
        if (kind === 'series') tidyEpisodes(data);
        const before = data;
        data = await this._enrich(kind, data, base);
        data = await this._enrichCatalog(kind, data, base);
        data = await this._enrichOmdb(kind, data, base);
        this._details.set(key, data);
        this._noteFeatures(kind, id, data);
        if (!fresh || data !== before) { Promise.resolve(db.put('details', key, { data, updated: fresh ? cached.updated : Date.now() })).catch(() => {}).then(() => { this._people = null; }); }
        return data;
    }

    /** Ficha ya cargada en esta sesión (o null): para pintar sin esperar a la red. */
    peekInfo(kind, id) {
        const have = this._details.get(this.id + ':' + kind + ':' + id) || null;
        if (have && this.editApply) this.editApply(kind, id, have);
        return have;
    }

    async vodInfo(id) {
        return this._detailInfo('vod', id);
    }

    async seriesInfo(id) {
        return this._detailInfo('series', id);
    }

    /**
     * Completa con TMDB los episodios de una temporada (los proveedores casi nunca traen la imagen): fotograma, sinopsis,
     * duración y, si el proveedor solo pone «Episodio 3», el título. Lo de TMDB manda cuando existe; si no, se queda lo del
     * proveedor. Devuelve true si algo cambió (para repintar). Una vez por temporada y sesión.
     */
    async fillSeason(info, number) {
        const src = this.seasonSource;
        if (!src || !info || !Array.isArray(info.seasons)) return false;
        // Sin el id de TMDB (la ficha del proveedor venía completa y no se consultó), se averigua por el título (ya cacheado).
        const policy = this._tmdbPolicy(info);
        if (policy === 'skip') return false; // vídeos personales: ni ids ni títulos de TMDB
        if (!info.tmdb && this.enricher) {
            try { const x = await this.enricher('series', info); if (x && x.tmdbId && (policy === 'free' || confidentMatch(info, x))) info.tmdb = x.tmdbId; } catch (e) { /* sin red */ }
        }
        if (!info.tmdb) return false;
        const key = this.id + ':' + info.id + ':' + number;
        if (!this._seasonDone) this._seasonDone = new Set();
        if (this._seasonDone.has(key)) return false;
        this._seasonDone.add(key);
        const season = info.seasons.find((s) => s.number === Number(number));
        if (!season || !season.episodes || !season.episodes.length) return false;
        let data = null;
        try {
            data = await src(info.tmdb, Number(number));
        } catch (e) {
            this._seasonDone.delete(key); // sin red: se vuelve a intentar al volver a verla
            return false;
        }
        const list = data && Array.isArray(data.episodes) ? data.episodes : [];
        if (!list.length) return false;
        const byNum = new Map(list.map((e) => [e.num, e]));
        let changed = false;
        season.episodes.forEach((ep) => {
            const x = byNum.get(Number(ep.num));
            if (!x) return;
            if (x.still && ep.thumb !== x.still) { ep.thumb = x.still; changed = true; }
            if (x.overview && ep.plot !== x.overview) { ep.plot = x.overview; changed = true; }
            if (x.runtime && !ep.durationSecs) { ep.durationSecs = x.runtime * 60; changed = true; }
            if (x.name && (!ep.title || /^(episodio|episode|capítulo|capitulo|ep\.?)\s*\d+$/i.test(String(ep.title).trim()))) { ep.title = x.name; changed = true; }
        });
        return changed;
    }

    findEpisode(seriesInfo, season, num) {
        if (!seriesInfo || !seriesInfo.seasons) return null;
        const s = seriesInfo.seasons.find((x) => x.number === Number(season));
        if (!s) return null;
        return s.episodes.find((e) => e.num === Number(num)) || null;
    }

    nextEpisode(seriesInfo, episode) {
        if (!seriesInfo || !seriesInfo.seasons || !episode) return null;
        const seasons = seriesInfo.seasons;
        const sIdx = seasons.findIndex((s) => s.number === episode.season);
        if (sIdx === -1) return null;
        const list = seasons[sIdx].episodes;
        const eIdx = list.findIndex((e) => e.id === episode.id);
        if (eIdx !== -1 && list[eIdx + 1]) return list[eIdx + 1];
        for (let i = sIdx + 1; i < seasons.length; i++) {
            if (seasons[i].episodes.length) return seasons[i].episodes[0];
        }
        return null;
    }

    /* ---------------- URLs de reproducción ---------------- */

    liveUrl(channel) {
        return net.streamUrl(this.rawLiveUrl(channel));
    }

    /** URL del canal en el proveedor, sin proxy (para grabar en el servidor). */
    rawLiveUrl(channel) {
        return channel.url || this._client(channel).liveUrl(this._rawId(channel), settings.get('liveFormat'));
    }

    vodUrl(movie) {
        return net.streamUrl(this.rawVodUrl(movie));
    }

    /** URL del proveedor sin pasar por el proxy (para diagnósticos). */
    rawVodUrl(movie) {
        return movie.url || this._client(movie).vodUrl(this._rawId(movie), movie.ext, movie);
    }

    episodeUrl(episode) {
        return net.streamUrl(this.rawEpisodeUrl(episode));
    }

    rawEpisodeUrl(episode) {
        return episode.url || this._client(episode).seriesUrl(this._rawId(episode), episode.ext, episode);
    }

    catchupUrl(channel, program) {
        const client = this._client(channel);
        if (!client || !channel.archive) return null;
        const minutes = Math.ceil((program.stop - program.start) / 60000);
        return net.streamUrl(client.timeshiftUrl(this._rawId(channel), new Date(program.start), minutes));
    }

    canCatchup(channel, program) {
        if (!channel || !channel.archive || !program) return false;
        const now = Date.now();
        const limit = (channel.archiveDays || 1) * 86400000;
        return program.start < now && program.start > now - limit;
    }

    /* ---------------- Control parental ---------------- */

    isLockedCategory(kind, categoryId) {
        const parental = settings.get('parental');
        if (!this.isKids && (!parental || !parental.enabled)) return false;
        const custom = (parental && parental.categories) || {};
        const key = kind + ':' + categoryId;
        if (custom[key] === true) return true;
        if (custom[key] === false) return false;
        const cat = this.category(kind, categoryId);
        return !!(cat && cat.adult);
    }

    async setPin(pin) {
        const parental = Object.assign({}, settings.get('parental'), { pinHash: await sha256('iptv-pin:' + pin) });
        settings.set('parental', parental);
    }

    async checkPin(pin) {
        const parental = settings.get('parental');
        if (!parental.pinHash) return true;
        return (await sha256('iptv-pin:' + pin)) === parental.pinHash;
    }

    async unlock(pin) {
        if (this.isKids) return false;
        const ok = await this.checkPin(pin);
        if (ok) {
            this.parentalUnlocked = true;
            this.emit('change');
        }
        return ok;
    }

    lock() {
        this.parentalUnlocked = false;
        this.emit('change');
    }

    /* ---------------- Mantenimiento ---------------- */

    async clearCache() {
        await catalogStore.remove(this.id);
        await db.delete('epg', this.id);
        this._people = null;
        await db.deleteByPrefix('details', this.id + ':');
        this._details.clear();
        epg.reset();
        net.resetMemo();
    }

    async purgeProfile(profileId) {
        await catalogStore.remove(profileId);
        await db.delete('epg', profileId);
        await db.delete('progress', profileId);
        await db.deleteByPrefix('progress', profileId + ':');
        await db.deleteByPrefix('favorites', profileId + ':');
        await db.deleteByPrefix('meta', profileId + ':');
        this._people = null;
        await db.deleteByPrefix('details', profileId + ':');
    }

    /** Vacía el historial de un espectador (lista, progreso, recientes) en local y en la cuenta. */
    async clearViewerData(viewerId) {
        Object.keys(this._pushTimers).forEach((k) => { clearTimeout(this._pushTimers[k]); delete this._pushTimers[k]; });
        if (this.sync) await this.sync.clearViewerData(viewerId);
        await this.purgeViewer(viewerId);
        if (this.viewer && this.viewer.id === viewerId) {
            this.favorites = new Set();
            this.hidden = new Set();
            this.progress = {};
            this.recent = [];
            const key = this.userKey;
            await Promise.all([
                db.put('favorites', key + ':favorites', []),
                db.put('favorites', key + ':hidden', []),
                db.put('progress', key, {}),
                db.put('meta', key + ':recent', [])
            ]);
            this.emit('progress');
            this.emit('change');
        }
    }

    /** Borra los datos locales de un espectador eliminado (en todos los proveedores). */
    async purgeViewer(viewerId) {
        const suffix = ':' + viewerId;
        for (const store of ['progress', 'favorites', 'meta']) {
            const rows = await db.getAll(store);
            await Promise.all(rows.filter((r) => String(r.key).indexOf(suffix + ':') !== -1 || String(r.key).endsWith(suffix)).map((r) => db.delete(store, r.key)));
        }
    }
}

export const catalog = new Catalog();
